import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import {
  GitMerge,
  Waypoints,
  Plus,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Search,
  AlertCircle,
  Trash2,
  ArrowLeft,
  Loader2,
  GripVertical,
  Link2,
  Unlink,
  BarChart3,
  ArrowRight,
  Pencil,
  Eye,
  Send,
  ShieldCheck,
  XCircle,
  Globe,
  Archive,
  Ban,
  MessageSquare,
  AlertTriangle,
  MousePointerClick,
  ZapOff,
  Upload,
  Import,
  FileText,
  X,
  CheckCircle,
  RefreshCw,
  Check,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { useToastStore } from "../../stores/toastStore";
import { RBACButton, RBACAction } from "../components/RBACButton";
import { useRBACPermission } from "../../hooks/useRBACPermission";
import { DataTable, Column } from "../components/DataTable";
import {
  EntityCardGrid,
  StatTile,
  StatTiles,
  MetaDates,
  CardActions,
  TitleTooltip,
} from "../components/EntityCardGrid";
import { StatusBadge } from "../components/StatusBadge";
import { Dropdown } from "../components/Dropdown";
import { PageSizeSelector } from "../components/PageSizeSelector";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Tip,
} from "../components/ui/tooltip";
import {
  Modal,
  FormField,
  FormInput,
  ModalFooterButtons,
} from "../components/Modal";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { getAPI, downloadFile } from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import {
  listMappingCollections,
  getMappingCollection,
  createMappingCollection,
  deleteMappingCollection,
  renameDocument,
  addMappingToCollection,
  getAutoMappingSuggestions,
  applyAutoMappingSuggestions,
  createMappingEntry,
  updateMappingEntry,
  deleteMappingEntry,
  getMappingCoverage,
  transitionDocument,
  listCatalogs,
  getCatalog,
  importCatalog,
  commitImportSession,
  abortImportSession,
  getImportSession,
  validateImport,
  validateMappingImport,
  stageMappingImport,
  commitMappingImportSession,
  exportCatalogDoc,
  getExportJob,
  getExportDownloadUrl,
  type MappingCollectionSummary,
  type MappingCollectionDetail,
  type MappingResponse,
  type MappingRelationship,
  type CoverageData,
  type CatalogResponse,
  type ControlResponse,
  type GroupResponse,
  type ImportJobResponse,
  type ImportSessionResponse,
  type ValidateResponse,
  type AutoMappingSuggestion,
  type AutoCreatedCollection,
} from "../../api/frameworksApi";
import { getErrorMessageWithGuidance } from "../../api/errorMap";

/* ── Helpers ── */

function fmtDate(iso: string): string {
  if (!iso) return "—";
  return iso.split("T")[0].split("-").reverse().join("/");
}

/** A collection is "derived" if it was generated by Auto-derive (title prefix). */
function isDerivedCollection(row: { title?: string }): boolean {
  return /^auto-?derived[:\s]/i.test(row.title ?? "");
}

function extractAllControls(groups?: GroupResponse[]): ControlResponse[] {
  if (!groups) return [];
  const result: ControlResponse[] = [];
  for (const g of groups) {
    for (const c of g.controls ?? []) {
      result.push(c);
      if (c.enhancements) result.push(...c.enhancements);
    }
    result.push(...extractAllControls(g.groups));
  }
  return result;
}

function normalizeResourceToken(value?: string | null): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("#")) return trimmed.slice(1);
  const hashIndex = trimmed.lastIndexOf("#");
  if (hashIndex >= 0 && hashIndex < trimmed.length - 1) {
    return trimmed.slice(hashIndex + 1);
  }
  const withoutQuery = trimmed.split("?")[0].replace(/\/+$/, "");
  const parts = withoutQuery.split("/").filter(Boolean);
  return parts[parts.length - 1] ?? trimmed;
}

function buildResourceHref(value: string): string {
  const token = normalizeResourceToken(value);
  return token ? `#${token}` : "";
}

function resolveCatalogByToken(
  token: string,
  catalogs: CatalogResponse[],
): CatalogResponse | undefined {
  const normalized = normalizeResourceToken(token);
  if (!normalized) return undefined;

  return catalogs.find((catalog) => {
    const candidates = [
      catalog.id,
      catalog.documentId,
      buildResourceHref(catalog.id),
      buildResourceHref(catalog.documentId),
    ].filter(Boolean);

    return candidates.some(
      (candidate) => normalizeResourceToken(candidate) === normalized,
    );
  });
}

function catalogSelectValue(catalog: CatalogResponse): string {
  return catalog.documentId || catalog.id;
}

function getControlKey(
  entry: Partial<MappingResponse["entries"][number]> & Record<string, unknown>,
  side: "source" | "target",
): string {
  const directKey =
    side === "source" ? entry.sourceControlId : entry.targetControlId;
  if (typeof directKey === "string" && directKey.trim()) return directKey;

  const candidates =
    side === "source"
      ? [
          entry.sourceControlHref,
          entry.sourceHref,
          entry.sourceId,
          entry.sourceControl,
        ]
      : [
          entry.targetControlHref,
          entry.targetHref,
          entry.targetId,
          entry.targetControl,
        ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return normalizeResourceToken(candidate);
    }
  }

  return "";
}

function getMappingCatalogToken(
  mapping: MappingResponse,
  side: "source" | "target",
): string {
  const directValue =
    side === "source" ? mapping.sourceResourceHref : mapping.targetResourceHref;

  if (typeof directValue === "string" && directValue.trim()) {
    return normalizeResourceToken(directValue);
  }

  const m = mapping as unknown as Record<string, unknown>;
  const fallbackCandidates =
    side === "source"
      ? [m.sourceCatalogId, m.sourceDocumentId, m.sourceId, m.sourceHref]
      : [m.targetCatalogId, m.targetDocumentId, m.targetId, m.targetHref];

  for (const candidate of fallbackCandidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return normalizeResourceToken(candidate);
    }
  }

  return "";
}

const COVERAGE_CHIPS_PER_PAGE = 12;
const COVERAGE_GAPS_PER_PAGE = 10;

/* ── Relationship display ── */

const RELATIONSHIPS: {
  value: MappingRelationship;
  label: string;
  color: string;
}[] = [
  {
    value: "equivalent-to",
    label: "Equivalent To",
    color:
      "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  },
  {
    value: "equal-to",
    label: "Equal To",
    color:
      "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  },
  {
    value: "subset-of",
    label: "Subset Of",
    color: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-400",
  },
  {
    value: "superset-of",
    label: "Superset Of",
    color:
      "bg-indigo-100 dark:bg-violet-500/15 text-indigo-700 dark:text-violet-400",
  },
  {
    value: "intersects-with",
    label: "Intersects With",
    color:
      "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400",
  },
  {
    value: "no-relationship",
    label: "No Relationship",
    color:
      "bg-slate-100 dark:bg-white/[0.06] text-slate-500 dark:text-white/40",
  },
];

function relColor(rel: MappingRelationship): string {
  return (
    RELATIONSHIPS.find((r) => r.value === rel)?.color ??
    "bg-slate-100 text-slate-500"
  );
}

function relLabel(rel: MappingRelationship): string {
  return RELATIONSHIPS.find((r) => r.value === rel)?.label ?? rel;
}

/* ── Table row type ── */
interface CollectionRow {
  [key: string]: unknown;
  id: string;
  documentId: string;
  title: string;
  mappingCount: number;
  entryCount: number;
  lifecycleStatus: string;
  derived: boolean;
  derivedFromTitle: string;
  createdAt: string;
  updatedAt: string;
  /* Raw epoch millis for chronological sorting (display uses the formatted strings). */
  createdAtRaw: number;
  updatedAtRaw: number;
}

function toRow(c: MappingCollectionSummary): CollectionRow {
  const fromTitles = [
    c.derivedFromCollectionATitle,
    c.derivedFromCollectionBTitle,
  ].filter((t): t is string => !!t && !!t.trim());
  return {
    id: c.id,
    documentId: c.documentId,
    title: c.title ?? "Untitled Collection",
    mappingCount: c.mappingCount ?? 0,
    entryCount: c.entryCount ?? 0,
    lifecycleStatus: c.lifecycleStatus ?? "draft",
    derived: c.derived ?? false,
    derivedFromTitle: fromTitles.join(" + "),
    createdAt: fmtDate(c.createdAt),
    updatedAt: fmtDate(c.updatedAt),
    createdAtRaw: c.createdAt ? new Date(c.createdAt).getTime() || 0 : 0,
    updatedAtRaw: c.updatedAt ? new Date(c.updatedAt).getTime() || 0 : 0,
  };
}

function statusBadge(status: string) {
  const s = String(status).toLowerCase() as any;
  return <StatusBadge status={s} dot={false} />;
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Main Component
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

export function Mappings() {
  const navigate = useNavigate();
  const { id: mappingIdFromUrl } = useParams<{ id: string }>();

  /* ── List state ── */
  const [collections, setCollections] = useState<CollectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [listSearch, setListSearch] = useState("");
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(12);
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [derivedFilter, setDerivedFilter] = useState<
    "all" | "mapped" | "derived"
  >("all");
  const [colTypeFilter, setColTypeFilter] = useState<"" | "mapped" | "derived">(
    "",
  );
  const [colSortKey, setColSortKey] = useState<string | null>(null);
  const [colSortDir, setColSortDir] = useState<"asc" | "desc">("asc");
  const handleColSort = (key: string) => {
    if (colSortKey === key)
      setColSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setColSortKey(key);
      setColSortDir("asc");
    }
  };

  /* ── Detail state ── */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<CollectionRow | null>(null);
  const [detail, setDetail] = useState<MappingCollectionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  /* ── Auto chain-mapping ── */
  const [autoSuggestions, setAutoSuggestions] = useState<
    AutoMappingSuggestion[]
  >([]);
  const [applyingAll, setApplyingAll] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState(false);

  /* ── Catalogs for dropdowns ── */
  const [catalogs, setCatalogs] = useState<CatalogResponse[]>([]);
  const [catalogsLoaded, setCatalogsLoaded] = useState(false);

  /* Only published catalogs can be linked in a control mapping */
  const publishedCatalogs = useMemo(
    () =>
      catalogs.filter(
        (c) => (c.lifecycleStatus ?? "").toLowerCase() === "published",
      ),
    [catalogs],
  );

  /* Dropdown options with fallbacks so a catalog missing id/title never
     produces an unusable option. Entries without an id are skipped, and a
     readable label is always shown. */
  const catalogOptions = useMemo(
    () =>
      publishedCatalogs
        .filter((c) => Boolean(c?.id))
        .map((c) => ({
          value: c.id,
          label: c.title?.trim() || c.id || "Untitled catalog",
        })),
    [publishedCatalogs],
  );

  /* ── Current mapping (source↔target link) ── */
  const [activeMapping, setActiveMapping] = useState<MappingResponse | null>(
    null,
  );

  /* ── Source & target controls ── */
  const [sourceControls, setSourceControls] = useState<ControlResponse[]>([]);
  const [targetControls, setTargetControls] = useState<ControlResponse[]>([]);
  const [sourceSearch, setSourceSearch] = useState("");
  const [targetSearch, setTargetSearch] = useState("");
  const [sourceLoading, setSourceLoading] = useState(false);
  const [targetLoading, setTargetLoading] = useState(false);

  /* ── Catalog selection for linking ── */
  const [sourceCatalogId, setSourceCatalogId] = useState("");
  const [targetCatalogId, setTargetCatalogId] = useState("");

  /* ── DnD state ── */
  const [draggingControl, setDraggingControl] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  /* ── Inline mapping panel state ── */
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [selectedRelType, setSelectedRelType] = useState<
    MappingRelationship | ""
  >("");
  const [deleteMode, setDeleteMode] = useState(false);
  const [markedForDelete, setMarkedForDelete] = useState<string[]>([]);
  const [deletingEntries, setDeletingEntries] = useState(false);
  const [savingEntry, setSavingEntry] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

  /* ── Legacy single-pair state kept for edit-entry modal ── */
  const [showRelPicker, setShowRelPicker] = useState(false);
  const [pendingSource, setPendingSource] = useState("");
  const [pendingTarget, setPendingTarget] = useState("");

  /* ── Create collection modal ── */
  const [showCreate, setShowCreate] = useState(false);
  const [createTitle, setCreateTitle] = useState("");
  const [creating, setCreating] = useState(false);

  /* ── Organization state ── */
  const [accessibleOrgs, setAccessibleOrgs] = useState<any[]>([]);
  const [accessibleOrgsLoading, setAccessibleOrgsLoading] = useState(false);
  const [createOrgId, setCreateOrgId] = useState("");
  const [importOrgId, setImportOrgId] = useState("");

  /* ── Delete modal ── */
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  /* ── Coverage panel ── */
  const [showCoverage, setShowCoverage] = useState(false);
  const [coverage, setCoverage] = useState<CoverageData | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [unmappedPage, setUnmappedPage] = useState(1);
  const [uncoveredPage, setUncoveredPage] = useState(1);
  const [gapsPage, setGapsPage] = useState(1);
  const [expandedRelatedEntries, setExpandedRelatedEntries] = useState<
    Record<string, boolean>
  >({});
  const [expandedSources, setExpandedSources] = useState<
    Record<string, boolean>
  >({});
  const [removingEntryId, setRemovingEntryId] = useState<string | null>(null);
  const [updatingEntryId, setUpdatingEntryId] = useState<string | null>(null);
  const [mappingSearch, setMappingSearch] = useState("");
  const [mappingPage, setMappingPage] = useState(1);
  const [mappingPageSize, setMappingPageSize] = useState(10);

  /* ── Inline rename state ── */
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);

  /* ── Edit mode (false = read-only view, true = edit workspace) ── */
  const [editMode, setEditMode] = useState(false);

  /* ── Lifecycle transitions ── */
  const [showTransition, setShowTransition] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState("");
  const [transitionLabel, setTransitionLabel] = useState("");
  const [transitionComments, setTransitionComments] = useState("");
  const [transitioning, setTransitioning] = useState(false);
  const [transitionRequireComments, setTransitionRequireComments] =
    useState(false);

  /* ── Export state ── */
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportModalDocId, setExportModalDocId] = useState<string | null>(null);
  const [exportModalName, setExportModalName] = useState("");
  const [exportFormat, setExportFormat] = useState<string>("");

  /* ── OSCAL Import pipeline state ── */
  const [showImport, setShowImport] = useState(false);
  const [importStep, setImportStep] = useState<
    | "select"
    | "url"
    | "validating"
    | "validated"
    | "importing"
    | "findings"
    | "committed"
  >("select");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importUrl, setImportUrl] = useState("");
  const [importMode, setImportMode] = useState<"full" | "delta">("full");
  const [importError, setImportError] = useState<string | null>(null);
  const [importErrorTitle, setImportErrorTitle] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportJobResponse | null>(
    null,
  );
  const [validateResult, setValidateResult] = useState<ValidateResponse | null>(
    null,
  );
  const [validating, setValidating] = useState(false);

  /* ── Control mapping import state ──
     Catalog picks are conditional: a catalog id is only chosen (and later sent)
     for a side the file couldn't resolve, as reported by validate findings. */
  const [importCatalogs, setImportCatalogs] = useState<CatalogResponse[]>([]);
  const [importCatalogsError, setImportCatalogsError] = useState(false);
  const [importSourceCatalogId, setImportSourceCatalogId] = useState("");
  const [importTargetCatalogId, setImportTargetCatalogId] = useState("");
  const [staging, setStaging] = useState(false);
  // True when the active import session came from the mapping flow, so commit
  // resends only the picked catalog ids and lands back on the mappings list.
  const [isMappingSession, setIsMappingSession] = useState(false);
  // Monotonic id so a slow, stale validate response can't overwrite a newer one
  // (the user can pick catalogs faster than the API responds).
  const validateSeqRef = useRef(0);

  /* ── Import Session modal state ── */
  const [showSession, setShowSession] = useState(false);
  const [sessionData, setSessionData] = useState<ImportSessionResponse | null>(
    null,
  );
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [aborting, setAborting] = useState(false);

  /* ── SVG line refs ── */
  const workspaceRef = useRef<HTMLDivElement>(null);
  const sourceRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const targetRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [linePositions, setLinePositions] = useState<
    {
      sourceId: string;
      targetId: string;
      rel: MappingRelationship;
      entryId: string;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    }[]
  >([]);

  /* ── Fetch collections ── */
  const fetchCollections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listMappingCollections();
      setCollections((res.data ?? []).map(toRow));
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load mapping collections",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  /* ── Fetch accessible organizations ── */
  const fetchAccessibleOrgs = async (module: string, action: string) => {
    setAccessibleOrgsLoading(true);
    try {
      const res = await getAPI(
        `${API_URL.ACCESSIBLE_ORGS}?module=${module}&action=${action}&org_type=ORGANIZATION&org_type=GROUP`,
      );
      const orgs = res?.data?.organizations ?? [];
      setAccessibleOrgs(orgs);
      if (orgs.length > 0 && !createOrgId) {
        setCreateOrgId(orgs[0].id);
      }
      if (orgs.length > 0 && !importOrgId) {
        setImportOrgId(orgs[0].id);
      }
    } catch {
      setAccessibleOrgs([]);
    } finally {
      setAccessibleOrgsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccessibleOrgs("control_mapping", "create");
  }, []);

  /* ── Delete listener ── */
  useEffect(() => {
    const handler = (e: Event) => setDeleteId((e as CustomEvent).detail);
    window.addEventListener("delete-collection", handler);
    return () => window.removeEventListener("delete-collection", handler);
  }, []);

  /* ── Load catalogs once ── */
  const loadCatalogs = useCallback(async (): Promise<CatalogResponse[]> => {
    if (catalogsLoaded && catalogs.length > 0) return catalogs;
    try {
      const res = await listCatalogs();
      const nextCatalogs = res.data ?? [];
      setCatalogs(nextCatalogs);
      setCatalogsLoaded(true);
      return nextCatalogs;
    } catch {
      return catalogs;
    }
  }, [catalogs, catalogsLoaded]);

  /* ── Auto chain-mapping: fetch pending suggestions ── */
  const fetchAutoSuggestions = useCallback(async (id: string) => {
    try {
      const res = await getAutoMappingSuggestions(id);
      setAutoSuggestions(res.data ?? []);
    } catch {
      // suggestions are optional — silently ignore if unavailable
      setAutoSuggestions([]);
    }
  }, []);

  /* ── Auto chain-mapping: show informational toasts for auto-created collections ── */
  const notifyAutoCreated = useCallback(
    (autoCreated?: AutoCreatedCollection[]) => {
      if (!autoCreated || autoCreated.length === 0) return;
      const store = useToastStore.getState();
      for (const ac of autoCreated) {
        store.showInfo(
          "Derived mapping created",
          `${ac.derivedCollectionTitle} — ${ac.derivedEntryCount} entries mapped, ${ac.skippedEntryCount} skipped.`,
        );
        if (ac.skippedEntryCount > 0) {
          store.showWarning(
            "Some controls skipped",
            `${ac.skippedEntryCount} control${ac.skippedEntryCount === 1 ? "" : "s"} could not be mapped — no matching entry found in the target collection.`,
          );
        }
      }
    },
    [],
  );

  /* ── Load detail ── */
  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setActiveMapping(null);
    setSourceControls([]);
    setTargetControls([]);
    setSourceCatalogId("");
    setTargetCatalogId("");
    setCoverage(null);
    setAutoSuggestions([]);
    setDismissedSuggestions(false);

    try {
      const res = await getMappingCollection(id);
      setDetail(res.data);
      // If there's already a mapping, set it as active
      if (res.data.mappings?.length > 0) {
        const m = res.data.mappings[0];
        setActiveMapping(m);
        const srcId = getMappingCatalogToken(m, "source");
        const tgtId = getMappingCatalogToken(m, "target");
        setSourceCatalogId(srcId);
        setTargetCatalogId(tgtId);
        if (srcId) loadControlsForCatalog(srcId, "source");
        if (tgtId) loadControlsForCatalog(tgtId, "target");
      }
      // Derived collections never have their own chain suggestions
      if (!res.data.derived) fetchAutoSuggestions(id);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load collection",
      );
    } finally {
      setDetailLoading(false);
    }
  }, []);

  /* ── Create ALL suggested chain derivations at once ── */
  const handleApplyAllSuggestions = async () => {
    if (!selectedId) return;
    setApplyingAll(true);
    try {
      const res = await applyAutoMappingSuggestions(selectedId);
      const created = res.data ?? [];
      if (created.length > 0) {
        useToastStore
          .getState()
          .showSuccess(
            "Derived mappings created",
            `${created.length} derived mapping${created.length === 1 ? "" : "s"} created.`,
          );
        const totalSkipped = created.reduce(
          (sum, c) => sum + (c.skippedEntryCount ?? 0),
          0,
        );
        if (totalSkipped > 0) {
          useToastStore
            .getState()
            .showWarning(
              "Some controls skipped",
              `${totalSkipped} control${totalSkipped === 1 ? "" : "s"} could not be mapped — no matching entry found in the target collection.`,
            );
        }
      } else {
        useToastStore
          .getState()
          .showInfo(
            "Already up to date",
            "All derived mappings already exist — nothing new was created.",
          );
      }
      // Hide the banner and refresh the list to show the new derived collections
      setAutoSuggestions([]);
      setDismissedSuggestions(true);
      fetchCollections();
    } catch (err: unknown) {
      const { message, guidance } = getErrorMessageWithGuidance(
        err,
        "Failed to create derived mappings",
      );
      useToastStore
        .getState()
        .showError(
          "Derived mapping failed",
          guidance ? `${message} ${guidance}` : message,
        );
    } finally {
      setApplyingAll(false);
    }
  };

  /* ── Load mapping from URL parameter ── */
  useEffect(() => {
    if (!mappingIdFromUrl) return;
    const mapping = collections.find((c) => c.id === mappingIdFromUrl);
    if (mapping) {
      setSelectedId(mappingIdFromUrl);
      setSelectedRow(mapping);
      setEditMode(false);
      loadDetail(mappingIdFromUrl);
      loadCatalogs();
    }
  }, [mappingIdFromUrl, collections]);

  /* ── Auto-load coverage when mapping is ready ── */
  useEffect(() => {
    if (!selectedId || !activeMapping) return;
    let cancelled = false;
    const loadCoverage = async () => {
      try {
        const res = await getMappingCoverage(selectedId);
        if (!cancelled) setCoverage(res.data);
      } catch {
        /* coverage may not be available yet */
      }
    };
    loadCoverage();
    return () => {
      cancelled = true;
    };
  }, [selectedId, activeMapping]);

  const loadControlsForCatalog = useCallback(
    async (catalogToken: string, side: "source" | "target") => {
      if (!catalogToken) return;
      const setLoading =
        side === "source" ? setSourceLoading : setTargetLoading;
      const setControls =
        side === "source" ? setSourceControls : setTargetControls;
      setLoading(true);
      try {
        const availableCatalogs =
          catalogs.length > 0 ? catalogs : await loadCatalogs();
        const resolvedCatalog = resolveCatalogByToken(
          catalogToken,
          availableCatalogs,
        );
        const lookupId =
          resolvedCatalog?.id ?? normalizeResourceToken(catalogToken);
        const res = await getCatalog(lookupId);
        const controls = extractAllControls(res.data?.groups);
        setControls(controls);
      } catch {
        setControls([]);
      } finally {
        setLoading(false);
      }
    },
    [catalogs, loadCatalogs],
  );

  /* ── Select collection from list ── */
  const handleSelectCollection = (row: CollectionRow) => {
    navigate(`/mappings/${row.id}`);
  };

  /* ── Create collection ── */
  const handleCreate = async () => {
    if (!createTitle.trim()) return;
    setCreating(true);
    try {
      const res = await createMappingCollection({
        title: createTitle.trim(),
        owningOrgUnitId: createOrgId || undefined,
      });
      toast.success("Mapping collection created");
      setShowCreate(false);
      setCreateTitle("");
      fetchCollections();
      // Navigate to detail
      setSelectedId(res.data.id);
      setSelectedRow(toRow(res.data));
      loadDetail(res.data.id);
      loadCatalogs();
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Failed to create collection",
      );
    } finally {
      setCreating(false);
    }
  };

  /* ── Delete collection ── */
  /* ── Rename handler ── */
  const handleRenameCollection = async (id: string, documentId: string) => {
    const trimmed = editingNameValue.trim();
    if (!trimmed) return;
    setSavingName(true);
    try {
      await renameDocument(documentId, trimmed);
      setCollections((prev) =>
        prev.map((c) => (c.id === id ? { ...c, title: trimmed } : c)),
      );
      if (selectedRow?.id === id)
        setSelectedRow((prev) => (prev ? { ...prev, title: trimmed } : prev));
      if (detail?.id === id)
        setDetail((prev) => (prev ? { ...prev, title: trimmed } : prev));
      setEditingNameId(null);
      useToastStore.getState().showSuccess("Success", "Collection renamed");
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to rename collection",
        );
    } finally {
      setSavingName(false);
    }
  };

  const handleDelete = async (reason?: string) => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteMappingCollection(deleteId, reason);
      toast.success("Collection deleted");
      setDeleteId(null);
      setDeleteReason("");
      if (selectedId === deleteId) {
        setSelectedId(null);
        setDetail(null);
      }
      fetchCollections();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  /* ── Link catalogs ── */
  const handleLinkCatalogs = async () => {
    if (!selectedId || !sourceCatalogId || !targetCatalogId) return;
    try {
      const res = await addMappingToCollection(selectedId, {
        sourceResourceHref: buildResourceHref(sourceCatalogId),
        targetResourceHref: buildResourceHref(targetCatalogId),
        sortOrder: 1,
      });
      setActiveMapping(res.data);
      toast.success("Catalogs linked");
      // Inform the user of any auto-derived chain mappings created by the backend
      notifyAutoCreated(res.data.autoCreated);
      // reload detail
      loadDetail(selectedId);
      loadControlsForCatalog(sourceCatalogId, "source");
      loadControlsForCatalog(targetCatalogId, "target");
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Failed to link catalogs",
      );
    }
  };

  /* ── Drag and drop ── */
  const handleDragStart = (controlId: string) => {
    setDraggingControl(controlId);
  };

  const handleDragOver = (e: React.DragEvent, controlId: string) => {
    e.preventDefault();
    setDropTarget(controlId);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = (e: React.DragEvent, targetControlId: string) => {
    e.preventDefault();
    setDropTarget(null);
    if (!draggingControl) return;
    setEditingEntryId(null);
    setPendingSource(draggingControl);
    setPendingTarget(targetControlId);
    setShowRelPicker(true);
    setDraggingControl(null);
  };

  const handleDragEnd = () => {
    setDraggingControl(null);
    setDropTarget(null);
  };

  /* ── Create entry (legacy — used by edit-entry modal) ── */
  const handleCreateEntry = async (relationship: MappingRelationship) => {
    if (!selectedId || !activeMapping) return;
    setSavingEntry(true);
    try {
      await createMappingEntry(selectedId, activeMapping.id, {
        sourceControlId: pendingSource,
        targetControlId: pendingTarget,
        relationship,
        sortOrder: (activeMapping.entries?.length ?? 0) + 1,
      });
      toast.success(`Mapped ${pendingSource} → ${pendingTarget}`);
      setShowRelPicker(false);
      loadDetail(selectedId);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Failed to create mapping",
      );
    } finally {
      setSavingEntry(false);
    }
  };

  /* ── Save all inline selections (multi-source × multi-target) ── */
  const handleSaveAll = async () => {
    if (
      !selectedId ||
      !activeMapping ||
      selectedSources.length === 0 ||
      selectedTargets.length === 0 ||
      !selectedRelType
    )
      return;
    setSavingEntry(true);
    try {
      let sortOrder = (activeMapping.entries?.length ?? 0) + 1;
      for (const src of selectedSources) {
        for (const tgt of selectedTargets) {
          await createMappingEntry(selectedId, activeMapping.id, {
            sourceControlId: src,
            targetControlId: tgt,
            relationship: selectedRelType as MappingRelationship,
            sortOrder: sortOrder++,
          });
        }
      }
      toast.success(
        `Saved ${selectedSources.length * selectedTargets.length} mapping(s)`,
      );
      setSelectedSources([]);
      setSelectedTargets([]);
      setSelectedRelType("");
      loadDetail(selectedId);
      // New entries may unlock a chain-mapping suggestion
      fetchAutoSuggestions(selectedId);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save mappings",
      );
    } finally {
      setSavingEntry(false);
    }
  };

  /* ── Bulk delete marked entries ── */
  const handleDeleteMarked = async () => {
    if (!selectedId || !activeMapping || markedForDelete.length === 0) return;
    setDeletingEntries(true);
    try {
      for (const entryId of markedForDelete) {
        await deleteMappingEntry(selectedId, activeMapping.id, entryId);
      }
      toast.success(`Deleted ${markedForDelete.length} mapping(s)`);
      setMarkedForDelete([]);
      setDeleteMode(false);
      loadDetail(selectedId);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete mappings",
      );
    } finally {
      setDeletingEntries(false);
    }
  };

  /* ── Edit entry ── */
  const openEditEntryModal = (entryId: string) => {
    const entry = normalizedEntries.find((item) => item.id === entryId);
    if (!entry) return;
    setEditingEntryId(entry.id);
    setPendingSource(entry.sourceKey);
    setPendingTarget(entry.targetKey);
    setShowRelPicker(true);
  };

  const handleUpdateEntry = async (relationship: MappingRelationship) => {
    if (!selectedId || !activeMapping || !editingEntry) return;
    const previousMapping = activeMapping;
    setUpdatingEntryId(editingEntry.id);
    setShowRelPicker(false);
    setActiveMapping({
      ...activeMapping,
      entries: activeMapping.entries.map((entry) =>
        entry.id === editingEntry.id ? { ...entry, relationship } : entry,
      ),
    });

    try {
      await updateMappingEntry(selectedId, activeMapping.id, editingEntry.id, {
        sourceControlId: editingEntry.sourceKey,
        targetControlId: editingEntry.targetKey,
        relationship,
        sortOrder: editingEntry.sortOrder,
        remarks:
          ((editingEntry as unknown as Record<string, unknown>)
            .remarks as string) ?? null,
      });
      toast.success("Mapping updated");
      loadDetail(selectedId);
      getMappingCoverage(selectedId)
        .then((res) => setCoverage(res.data))
        .catch(() => {});
    } catch (err: unknown) {
      setActiveMapping(previousMapping);
      toast.error(
        err instanceof Error ? err.message : "Failed to update mapping",
      );
    } finally {
      setUpdatingEntryId(null);
      setEditingEntryId(null);
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (!selectedId || !activeMapping) return;
    const previousMapping = activeMapping;
    const previousCoverage = coverage;
    setRemovingEntryId(entryId);
    setActiveMapping({
      ...activeMapping,
      entries: activeMapping.entries.filter((entry) => entry.id !== entryId),
    });
    try {
      await deleteMappingEntry(selectedId, activeMapping.id, entryId);
      toast.success("Mapping entry removed");
      loadDetail(selectedId);
      getMappingCoverage(selectedId)
        .then((res) => setCoverage(res.data))
        .catch(() => {});
    } catch (err: unknown) {
      setActiveMapping(previousMapping);
      setCoverage(previousCoverage);
      toast.error(
        err instanceof Error ? err.message : "Failed to remove entry",
      );
    } finally {
      setRemovingEntryId(null);
    }
  };

  /* ── Coverage ── */
  const handleLoadCoverage = async () => {
    if (!selectedId) return;
    setShowCoverage(true);
    setUnmappedPage(1);
    setUncoveredPage(1);
    setGapsPage(1);
    // Only fetch if not already loaded — avoids overwriting the summary card's data
    if (coverage) return;
    setCoverageLoading(true);
    try {
      const res = await getMappingCoverage(selectedId);
      setCoverage(res.data);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load coverage",
      );
    } finally {
      setCoverageLoading(false);
    }
  };

  /* ── Lifecycle transitions ── */
  const lifecycleActions: Record<
    string,
    {
      label: string;
      target: string;
      color: string;
      icon: React.ReactNode;
      requireComments?: boolean;
      warning?: string;
    }[]
  > = {
    draft: [
      {
        label: "Submit for Review",
        target: "in_review",
        color: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20",
        icon: <Send className="size-4" />,
      },
    ],
    in_review: [
      {
        label: "Approve",
        target: "approved",
        color: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20",
        icon: <ShieldCheck className="size-4" />,
      },
      {
        label: "Reject",
        target: "rejected",
        color: "bg-red-600 hover:bg-red-700 shadow-red-500/20",
        icon: <XCircle className="size-4" />,
        requireComments: true,
      },
    ],
    approved: [
      {
        label: "Publish",
        target: "published",
        color: "bg-purple-600 hover:bg-purple-700 shadow-purple-500/20",
        icon: <Globe className="size-4" />,
        warning: "This action will freeze the content permanently.",
      },
    ],
    rejected: [
      {
        label: "Submit for Review",
        target: "in_review",
        color: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20",
        icon: <Send className="size-4" />,
      },
    ],
    published: [
      {
        label: "Supersede",
        target: "superseded",
        color: "bg-orange-600 hover:bg-orange-700 shadow-orange-500/20",
        icon: <Archive className="size-4" />,
      },
      {
        label: "Withdraw",
        target: "withdrawn",
        color: "bg-rose-700 hover:bg-rose-800 shadow-rose-500/20",
        icon: <Ban className="size-4" />,
      },
    ],
    superseded: [],
    withdrawn: [],
  };

  const openTransitionModal = (
    target: string,
    label: string,
    requireComments = false,
  ) => {
    setTransitionTarget(target);
    setTransitionLabel(label);
    setTransitionComments("");
    setTransitionRequireComments(requireComments);
    setShowTransition(true);
  };

  const handleTransition = async () => {
    if (!selectedRow || !selectedId) return;
    if (transitionRequireComments && !transitionComments.trim()) return;
    setTransitioning(true);
    try {
      await transitionDocument(selectedRow.documentId, {
        targetStatus: transitionTarget,
        comments: transitionComments.trim() || undefined,
      });
      // Refresh list to get updated status
      const res = await listMappingCollections();
      const rows = (res.data ?? []).map(toRow);
      setCollections(rows);
      const updated = rows.find((r) => r.id === selectedRow.id);
      if (updated) setSelectedRow(updated);

      // Reload detail view to reflect the new status and available actions
      await loadDetail(selectedId);

      setShowTransition(false);
      toast.success(
        `Status changed to "${transitionTarget.replace(/_/g, " ")}"`,
      );
    } catch (err: unknown) {
      let errorMessage = "Transition failed";

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === "object" && err !== null) {
        const errorObj = err as Record<string, any>;
        if (
          errorObj.errors &&
          Array.isArray(errorObj.errors) &&
          errorObj.errors.length > 0
        ) {
          const firstError = errorObj.errors[0];
          errorMessage =
            firstError.detail ||
            firstError.title ||
            firstError.code ||
            errorMessage;
        } else if (errorObj.message) {
          errorMessage = errorObj.message;
        }
      }

      console.error("Transition error:", err);
      toast.error(errorMessage);
    } finally {
      setTransitioning(false);
    }
  };

  const currentLifecycleStatus = selectedRow?.lifecycleStatus ?? "draft";
  // Get permissions
  const hasUpdatePermission = useRBACPermission("control_mapping", "update");
  const hasApprovePermission = useRBACPermission("control_mapping", "approve");
  const hasPublishPermission = useRBACPermission("control_mapping", "publish");
  const hasDeletePermission = useRBACPermission("control_mapping", "delete");

  // Get lifecycle actions for current state with permission-based filtering
  const currentActions = (
    lifecycleActions[currentLifecycleStatus] ?? []
  ).filter((a) => {
    // Show "Submit for Review" only if user has update permission
    if (a.label === "Submit for Review") return hasUpdatePermission;
    // Show "Approve" and "Reject" only if user has approve permission
    if (["Approve", "Reject"].includes(a.label)) return hasApprovePermission;
    // Show "Publish" only if user has publish permission
    if (a.label === "Publish") return hasPublishPermission;
    // Show "Supersede" and "Withdraw" only if user has update permission
    if (["Supersede", "Withdraw"].includes(a.label)) return hasUpdatePermission;
    return true;
  });

  const isEditable =
    currentLifecycleStatus === "draft" || currentLifecycleStatus === "rejected";

  /* ── Export handler ── */
  const handleExportMapping = async (
    documentId: string,
    format: "json" | "xml" | "yaml",
  ) => {
    setExportingId(documentId);
    setExportModalDocId(null);
    setExportFormat(format);
    try {
      const mappingName =
        collections.find((c) => c.id === documentId)?.title || "mapping";
      const res = await exportCatalogDoc(documentId, format);
      let job = res.data;

      // Poll if pending
      if (job.status === "pending") {
        for (let i = 0; i < 30; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const poll = await getExportJob(job.jobId);
          job = poll.data;
          if (job.status !== "pending") break;
        }
      }

      if (job.status === "completed") {
        const downloadUrl = job.downloadUrl || getExportDownloadUrl(job.jobId);
        const downloadRes = await downloadFile(downloadUrl);
        if (!downloadRes) throw new Error("No response from download");

        // Extract documentTitle from response headers
        let documentTitle = mappingName;
        const xDocumentTitle = downloadRes.headers?.["x-document-title"];
        if (xDocumentTitle) {
          documentTitle = xDocumentTitle;
        } else {
          // Try to extract from Content-Disposition header
          const contentDisposition =
            downloadRes.headers?.["content-disposition"] || "";
          const filenameMatch = contentDisposition.match(
            /filename\*?=(?:"([^"]*)"|([^;,\s]*))/,
          );
          if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
            const filename = (filenameMatch[1] || filenameMatch[2]).split(
              ".",
            )[0];
            documentTitle = filename;
          }
        }

        // Get MIME type from API response Content-Type header first
        const rawContentType = downloadRes.headers?.["content-type"];
        let mimeType =
          typeof rawContentType === "string" ? rawContentType : "";
        if (!mimeType) {
          // Fallback: use selected format
          const mimeTypes: Record<string, string> = {
            json: "application/json",
            xml: "application/xml",
            yaml: "application/yaml",
          };
          mimeType = mimeTypes[format] || "application/octet-stream";
        }
        const blob = new Blob([downloadRes.data], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${documentTitle}.${format}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        useToastStore
          .getState()
          .showSuccess("Success", "Export ready — download started");
      } else if (job.status === "failed") {
        useToastStore.getState().showError("Error", "Export failed");
      } else {
        useToastStore
          .getState()
          .showError("Error", "Export timed out — please try again");
      }
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Export failed",
        );
    } finally {
      setExportingId(null);
    }
  };

  /* ── Import helpers ── */
  /** Convert a GitHub blob URL to its raw content URL */
  const toRawUrl = (url: string): string => {
    const ghMatch = url.match(
      /^https?:\/\/github\.com\/([^/]+\/[^/]+)\/blob\/(.+)$/,
    );
    if (ghMatch)
      return `https://raw.githubusercontent.com/${ghMatch[1]}/${ghMatch[2]}`;
    return url;
  };

  /** Check whether URL points to an allowed file type */
  const isAllowedUrl = (url: string): boolean =>
    /\.(json|yaml|yml|xml)(\?.*)?$/i.test(url.split("#")[0]);

  /** Fetch file from URL, then validate it */
  const fetchUrl = async () => {
    if (!importUrl.trim()) return;
    if (!isAllowedUrl(importUrl.trim())) {
      setImportError("URL must point to a .json, .yaml, or .xml file");
      useToastStore
        .getState()
        .showError("Error", "URL must point to a .json, .yaml, or .xml file");
      return;
    }
    setValidating(true);
    setImportError(null);
    setValidateResult(null);
    setImportFile(null);

    try {
      const rawUrl = toRawUrl(importUrl.trim());
      const response = await fetch(rawUrl);
      if (!response.ok)
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`,
        );
      const blob = await response.blob();
      const filename = importUrl.split("/").pop() || "document";
      const file = new File([blob], filename, {
        type: blob.type || "application/octet-stream",
      });
      setImportFile(file);

      const res = await validateImport(file);
      const docType = res.data.documentType.toLowerCase();
      if (docType !== "mapping" && docType !== "mapping-collection") {
        const msg = `Expected a mapping document but got "${res.data.documentType}"`;
        setImportError(msg);
        useToastStore.getState().showError("Error", msg);
      } else {
        setValidateResult(res.data);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to fetch";
      setImportError(msg);
      useToastStore.getState().showError("Error", msg);
    } finally {
      setValidating(false);
    }
  };

  /** Load catalogs for the import pickers, tracking a load error for retry. */
  const loadImportCatalogs = async () => {
    setImportCatalogsError(false);
    try {
      const res = await listCatalogs();
      setImportCatalogs(res.data ?? []);
    } catch {
      setImportCatalogs([]);
      setImportCatalogsError(true);
    }
  };

  // Reject files that clearly can't be an OSCAL mapping before hitting the API.
  const MAX_IMPORT_MB = 25;
  const rejectBadFile = (file: File): string | null => {
    if (file.size === 0) return "The selected file is empty.";
    if (file.size > MAX_IMPORT_MB * 1024 * 1024)
      return `File is too large (max ${MAX_IMPORT_MB} MB).`;
    if (!/\.(json|ya?ml|xml)$/i.test(file.name))
      return "Unsupported file type. Upload a .json, .yaml, or .xml mapping.";
    return null;
  };

  /** Validate the mapping file. On the first pass no catalog ids are sent;
   *  findings tell us which side(s) the file couldn't resolve, and the picker
   *  supplies ids for those side(s) on re-validate. Overrides carry the
   *  just-picked id because state updates are async. */
  const validateMappingFile = async (
    fileArg?: File,
    overrides?: { sourceCatalogId?: string; targetCatalogId?: string },
  ) => {
    const file = fileArg ?? importFile;
    if (!file) return;
    const badFile = rejectBadFile(file);
    if (badFile) {
      setValidating(false);
      setImportError(badFile);
      useToastStore.getState().showError("Error", badFile);
      return;
    }
    const picks = {
      sourceCatalogId:
        overrides?.sourceCatalogId ?? importSourceCatalogId ?? "",
      targetCatalogId:
        overrides?.targetCatalogId ?? importTargetCatalogId ?? "",
    };
    // Guard against out-of-order responses: only the latest call may commit
    // its result to state.
    const seq = ++validateSeqRef.current;
    const isStale = () => seq !== validateSeqRef.current;
    setValidating(true);
    setImportError(null);
    // Note: we intentionally DON'T clear validateResult here — keep the prior
    // findings/pickers on screen while re-validating so the panel doesn't blank
    // out. The new-file path clears it explicitly before calling this.
    // Keep the "Validating…" indicator visible for at least a beat so it
    // doesn't flicker past when the API responds quickly.
    const MIN_SPINNER_MS = 1200;
    const startedAt = Date.now();
    const holdSpinner = async () => {
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_SPINNER_MS)
        await new Promise((r) => setTimeout(r, MIN_SPINNER_MS - elapsed));
    };
    try {
      const res = await validateMappingImport(file, {
        sourceCatalogId: picks.sourceCatalogId || undefined,
        targetCatalogId: picks.targetCatalogId || undefined,
      });
      await holdSpinner();
      if (isStale()) return; // a newer validate superseded this one
      setValidateResult(res.data);
      // If the file couldn't resolve a catalog side, make sure the picker has
      // options to choose from.
      const needsPicker = (res.data.findings ?? []).some(
        (f) => f.code === "IMP-0203-E" || f.code === "IMP-0206-E",
      );
      if (needsPicker && importCatalogs.length === 0) {
        loadImportCatalogs();
      }
    } catch (err: unknown) {
      await holdSpinner();
      if (isStale()) return;
      const msg = err instanceof Error ? err.message : "Validation failed";
      setImportError(msg);
      useToastStore.getState().showError("Error", msg);
    } finally {
      if (!isStale()) setValidating(false);
    }
  };

  /** Stage the import (creates the session), then open the Commit modal which
   *  polls until the async validation lands on "validated". */
  const handleStageImport = async () => {
    if (!importFile || staging) return;
    if (accessibleOrgs.length > 0 && !importOrgId) {
      const msg = "Select an organization before importing.";
      setImportError(msg);
      useToastStore.getState().showError("Error", msg);
      return;
    }
    // Any in-flight validate is now irrelevant — don't let it flip state back.
    validateSeqRef.current++;
    setStaging(true);
    setImportError(null);
    try {
      const res = await stageMappingImport(importFile, {
        owningOrgUnitId: importOrgId || undefined,
        mode: importMode,
        sourceCatalogId: importSourceCatalogId || undefined,
        targetCatalogId: importTargetCatalogId || undefined,
      });
      if (!res?.data?.id) {
        throw new Error("Import staged but no session was returned.");
      }
      setIsMappingSession(true);
      setShowImport(false);
      openSessionModal(res.data.id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Import failed";
      setImportError(msg);
      useToastStore.getState().showError("Error", msg);
    } finally {
      setStaging(false);
    }
  };

  /** Step 2: Actual import after validation passes */
  const runImport = async () => {
    if (!importFile) return;
    setImportStep("importing");
    setImportError(null);
    try {
      const res = await importCatalog(
        importFile,
        importMode,
        importOrgId || undefined,
      );
      setImportResult(res.data);
      setShowImport(false);
      openSessionModal(res.data.id);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setImportErrorTitle(err.name !== "Error" ? err.name : "Import Failed");
        setImportError(err.message);
        useToastStore.getState().showError("Error", err.message);
      } else {
        setImportErrorTitle("Import Failed");
        setImportError("This file already exists");
        useToastStore.getState().showError("Error", "This file already exists");
      }
    }
  };

  /** Open session modal, fetch session, and start polling */
  const openSessionModal = async (sessionId: string) => {
    setShowSession(true);
    setSessionData(null);
    setSessionError(null);
    setSessionLoading(true);
    setCommitting(false);
    setAborting(false);
    try {
      const res = await getImportSession(sessionId);
      setSessionData(res.data);
    } catch (err: unknown) {
      setSessionError(
        err instanceof Error ? err.message : "Failed to fetch session",
      );
    } finally {
      setSessionLoading(false);
    }
  };

  /** Poll session until status is validated/failed/committed. Staging validates
   *  asynchronously, so poll quickly to flip Commit on as soon as it's ready.
   *  Give up after a bounded number of attempts so a stuck session doesn't
   *  poll forever. */
  useEffect(() => {
    if (!showSession || !sessionData) return;
    const status = sessionData.jobStatus.toLowerCase();
    if (status === "validated" || status === "failed" || status === "committed")
      return;
    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~60s at 1.5s intervals
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const res = await getImportSession(sessionData.id);
        setSessionData(res.data);
      } catch {
        // silently ignore transient polling errors
      }
      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(timer);
        setSessionError(
          "Validation is taking longer than expected. Close and try again, or check back shortly.",
        );
      }
    }, 1500);
    return () => clearInterval(timer);
  }, [showSession, sessionData]);

  /** Commit the import session */
  const handleCommitSession = async () => {
    if (!sessionData) return;
    setCommitting(true);
    setSessionError(null);
    try {
      if (isMappingSession) {
        // Mapping flow — resend only the catalog id(s) picked at validate time
        // (i.e. the side(s) the file couldn't resolve on its own).
        await commitMappingImportSession(sessionData.id, {
          owningOrgUnitId: importOrgId || undefined,
          sourceCatalogId: importSourceCatalogId || undefined,
          targetCatalogId: importTargetCatalogId || undefined,
        });
      } else {
        await commitImportSession(sessionData.id, importOrgId || undefined);
      }
      fetchCollections();
      useToastStore
        .getState()
        .showSuccess("Success", "Mapping Imported Successfully");
      setShowSession(false);
      setSessionData(null);
      resetImport();
      // Land on the mappings list page (not inside the new mapping doc).
      navigate("/mappings");
    } catch (err: unknown) {
      setSessionError(err instanceof Error ? err.message : "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  /** Abort the import session */
  const handleAbortSession = async () => {
    if (!sessionData) return;
    setAborting(true);
    setSessionError(null);
    try {
      await abortImportSession(sessionData.id);
      setShowSession(false);
      setSessionData(null);
      resetImport();
      fetchCollections();
    } catch (err: unknown) {
      setSessionError(err instanceof Error ? err.message : "Abort failed");
    } finally {
      setAborting(false);
    }
  };

  const resetImport = () => {
    setImportStep("select");
    setImportFile(null);
    setImportUrl("");
    setImportMode("full");
    setValidateResult(null);
    setValidating(false);
    setImportError(null);
    setImportErrorTitle(null);
    setImportResult(null);
    setImportSourceCatalogId("");
    setImportTargetCatalogId("");
    setStaging(false);
    setIsMappingSession(false);
    setImportCatalogsError(false);
    // Invalidate any in-flight validate so its late response is ignored.
    validateSeqRef.current++;
  };

  const normalizedEntries = useMemo(
    () =>
      (activeMapping?.entries ?? []).map((entry) => {
        const e = entry as unknown as Partial<
          MappingResponse["entries"][number]
        > &
          Record<string, unknown>;
        return {
          ...entry,
          sourceKey: getControlKey(e, "source"),
          targetKey: getControlKey(e, "target"),
        };
      }),
    [activeMapping?.entries],
  );

  const editingEntry =
    normalizedEntries.find((entry) => entry.id === editingEntryId) ?? null;

  /* ── Derived mapping-validation state (read findings by code) ── */
  const mv = (() => {
    const findings = validateResult?.findings ?? [];
    const byCode = (code: string) => findings.find((f) => f.code === code);
    const isError = (f: { severity?: string }) =>
      (f.severity ?? "").toLowerCase() === "error";
    const nameFromDetail = (d?: string) => {
      const m = d?.match(/'([^']+)'/);
      return m ? m[1] : undefined;
    };
    // Detail looks like: "Source catalog: 'IM8' (id 019f263b-…)."
    const idFromDetail = (d?: string) => {
      const m = d?.match(/id\s+([0-9a-fA-F-]{8,})/);
      return m ? m[1] : undefined;
    };
    const catalogMissingCodes = ["IMP-0203-E", "IMP-0206-E"];
    const errorFindings = findings.filter(isError);
    // Errors that require fixing the file itself (unknown ids / structural /
    // anything not a resolvable "pick a catalog" error).
    const blockingErrors = errorFindings.filter(
      (f) => !catalogMissingCodes.includes(f.code ?? ""),
    );
    return {
      hasResult: !!validateResult,
      missingSource: !!byCode("IMP-0203-E"),
      missingTarget: !!byCode("IMP-0206-E"),
      resolvedSourceName: nameFromDetail(byCode("IMP-0202-I")?.detail),
      resolvedTargetName: nameFromDetail(byCode("IMP-0205-I")?.detail),
      resolvedSourceId: idFromDetail(byCode("IMP-0202-I")?.detail),
      resolvedTargetId: idFromDetail(byCode("IMP-0205-I")?.detail),
      errorFindings,
      blockingErrors,
      hasErrors: errorFindings.length > 0,
      // Clean = validated with zero error findings → ready to stage/import.
      clean: !!validateResult && errorFindings.length === 0,
    };
  })();

  /* ── Compute SVG lines ── */
  const computeLines = useCallback(() => {
    if (!normalizedEntries.length || !workspaceRef.current) {
      setLinePositions([]);
      return;
    }
    const wsRect = workspaceRef.current.getBoundingClientRect();
    const lines: typeof linePositions = [];
    for (const entry of normalizedEntries) {
      const srcEl = sourceRefs.current.get(entry.sourceKey);
      const tgtEl = targetRefs.current.get(entry.targetKey);
      if (srcEl && tgtEl) {
        const sr = srcEl.getBoundingClientRect();
        const tr = tgtEl.getBoundingClientRect();
        lines.push({
          sourceId: entry.sourceKey,
          targetId: entry.targetKey,
          rel: entry.relationship,
          entryId: entry.id,
          x1: sr.right - wsRect.left,
          y1: sr.top + sr.height / 2 - wsRect.top,
          x2: tr.left - wsRect.left,
          y2: tr.top + tr.height / 2 - wsRect.top,
        });
      }
    }
    setLinePositions(lines);
  }, [normalizedEntries]);

  useEffect(() => {
    computeLines();
    const t = setTimeout(computeLines, 300);
    return () => clearTimeout(t);
  }, [computeLines, sourceControls, targetControls]);

  useEffect(() => {
    window.addEventListener("resize", computeLines);
    return () => window.removeEventListener("resize", computeLines);
  }, [computeLines]);

  /* ── Mapped control sets (for visual indicators) ── */
  const mappedSourceIds = new Set(
    normalizedEntries.map((entry) => entry.sourceKey).filter(Boolean),
  );
  const mappedTargetIds = new Set(
    normalizedEntries.map((entry) => entry.targetKey).filter(Boolean),
  );

  /* ── Filtered controls ── */
  const filteredSource = sourceControls.filter(
    (c) =>
      !sourceSearch ||
      c.controlId.toLowerCase().includes(sourceSearch.toLowerCase()) ||
      c.title.toLowerCase().includes(sourceSearch.toLowerCase()),
  );
  const filteredTarget = targetControls.filter(
    (c) =>
      !targetSearch ||
      c.controlId.toLowerCase().includes(targetSearch.toLowerCase()) ||
      c.title.toLowerCase().includes(targetSearch.toLowerCase()),
  );

  /* ── Get catalog name by id or documentId ── */
  const catalogName = (id: string) =>
    resolveCatalogByToken(id, catalogs)?.title ?? normalizeResourceToken(id);

  /* ── Filtered & paginated collections ── */
  const filteredCollections = useMemo(() => {
    let result = collections;

    if (listSearch.trim()) {
      const q = listSearch.toLowerCase();
      result = result.filter((c) => c.title.toLowerCase().includes(q));
    }

    if (filterStatus) {
      result = result.filter((c) => c.lifecycleStatus === filterStatus);
    }

    if (colTypeFilter === "derived") {
      result = result.filter((c) => isDerivedCollection(c));
    } else if (colTypeFilter === "mapped") {
      result = result.filter((c) => !isDerivedCollection(c));
    }

    return result;
  }, [collections, listSearch, filterStatus, colTypeFilter]);

  // Original serial number per row (position in the unsorted, filtered list) so
  // the "#" travels with its row — making it obvious when sorting reorders rows.
  const colOriginalIndexMap = new Map(
    filteredCollections.map((c, i) => [c.id, i]),
  );

  const sortedCollections = [...filteredCollections].sort((a, b) => {
    if (!colSortKey) return 0;
    // "type" is a derived label (Derived / Mapped), not a row field — resolve it.
    // Date columns sort by their raw epoch value, not the formatted string.
    const readVal = (row: CollectionRow): any =>
      colSortKey === "type"
        ? isDerivedCollection(row)
          ? "Derived"
          : "Mapped"
        : colSortKey === "createdAt"
          ? row.createdAtRaw
          : colSortKey === "updatedAt"
            ? row.updatedAtRaw
            : row[colSortKey as keyof typeof row];
    const aVal: any = readVal(a);
    const bVal: any = readVal(b);
    if (typeof aVal === "number")
      return colSortDir === "asc" ? aVal - bVal : bVal - aVal;
    return colSortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const paginatedCollections = useMemo(() => {
    const start = (listPage - 1) * listPageSize;
    return sortedCollections.slice(start, start + listPageSize);
  }, [sortedCollections, listPage, listPageSize]);

  const pagedUnmappedSourceIds = useMemo(() => {
    if (!coverage) return [];
    const start = (unmappedPage - 1) * COVERAGE_CHIPS_PER_PAGE;
    return coverage.unmappedSourceControlIds.slice(
      start,
      start + COVERAGE_CHIPS_PER_PAGE,
    );
  }, [coverage, unmappedPage]);

  const pagedUncoveredTargetIds = useMemo(() => {
    if (!coverage) return [];
    const start = (uncoveredPage - 1) * COVERAGE_CHIPS_PER_PAGE;
    return coverage.uncoveredTargetControlIds.slice(
      start,
      start + COVERAGE_CHIPS_PER_PAGE,
    );
  }, [coverage, uncoveredPage]);

  const pagedGaps = useMemo(() => {
    if (!coverage) return [];
    const start = (gapsPage - 1) * COVERAGE_GAPS_PER_PAGE;
    return coverage.gaps.slice(start, start + COVERAGE_GAPS_PER_PAGE);
  }, [coverage, gapsPage]);

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     DETAIL VIEW
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  if (mappingIdFromUrl && !selectedId) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="size-6 animate-spin text-indigo-500 dark:text-violet-400" />
      </div>
    );
  }

  if (selectedId) {
    const entryCount = activeMapping?.entries?.length ?? 0;
    const detailTitle =
      detail?.title ?? selectedRow?.title ?? "Mapping Collection";
    // Prefer backend-resolved catalog titles, fall back to the local catalog lookup
    const sourceCatalogTitle =
      activeMapping?.sourceCatalogTitle ?? catalogName(sourceCatalogId);
    const targetCatalogTitle =
      activeMapping?.targetCatalogTitle ?? catalogName(targetCatalogId);

    return (
      <div className="space-y-4">
        {/* Breadcrumb with Lifecycle Progression */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSelectedId(null);
              setSelectedRow(null);
              setDetail(null);
              navigate("/mappings");
            }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Mappings
          </button>
          <ChevronRight className="size-3.5 text-muted-foreground" />
          <span className="text-sm font-medium truncate">{detailTitle}</span>

          {/* Lifecycle Progression */}
          <div className="flex items-center gap-1 ml-3">
            {[
              { value: "draft", label: "Draft", color: "bg-emerald-500" },
              { value: "in_review", label: "In Review", color: "bg-emerald-500" },
              { value: "approved", label: "Approved", color: "bg-emerald-500" },
              { value: "published", label: "Published", color: "bg-emerald-500" },
            ].map((stage, idx, arr) => {
              const currentStatus = String(
                selectedRow?.lifecycleStatus,
              ).toLowerCase();
              const isPassed =
                arr.findIndex((s) => s.value === currentStatus) >= idx;

              return (
                <div key={stage.label} className="flex items-center gap-1">
                  <div
                    className={`size-1.5 rounded-full flex-shrink-0 ${isPassed ? stage.color : "bg-slate-400 dark:bg-slate-600"}`}
                  />
                  <span
                    className={`text-[10px] font-medium whitespace-nowrap ${isPassed ? "text-emerald-600 dark:text-emerald-400" : "text-slate-500 dark:text-white"}`}
                  >
                    {stage.label}
                  </span>
                  {idx < arr.length - 1 && (
                    <span className={`text-slate-400 dark:text-slate-600 font-light text-[8px]`}>–</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {detailLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-6 animate-spin text-indigo-500 dark:text-violet-400" />
          </div>
        ) : (
          <>
            {/* Header card */}
            <div className="rounded-lg border border-border bg-card px-5 py-3.5">
              <div className="flex flex-col gap-2">
                <div className="flex items-start gap-3 flex-wrap">
                    {editingNameId === selectedId ? (
                      <div className="flex items-center gap-2 min-w-0 flex-1 max-w-3xl">
                        <input
                          autoFocus
                          value={editingNameValue}
                          onChange={(e) => setEditingNameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")
                              handleRenameCollection(
                                selectedId!,
                                selectedRow!.documentId,
                              );
                            if (e.key === "Escape") setEditingNameId(null);
                          }}
                          className="text-[20px] font-semibold px-2 py-1 rounded border border-indigo-400 bg-card outline-none focus:ring-1 focus:ring-indigo-500 flex-1 min-w-0"
                          disabled={savingName}
                        />
                        <button
                          onClick={() =>
                            handleRenameCollection(
                              selectedId!,
                              selectedRow!.documentId,
                            )
                          }
                          disabled={savingName || !editingNameValue.trim()}
                          className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-500/10 text-green-600 dark:text-green-400 transition-colors disabled:opacity-40"
                          title="Save"
                        >
                          {savingName ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Check className="size-4" />
                          )}
                        </button>
                        <button
                          onClick={() => setEditingNameId(null)}
                          disabled={savingName}
                          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600 dark:text-red-400 transition-colors disabled:opacity-40"
                          title="Cancel"
                        >
                          <X className="size-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 min-w-0">
                        <h1 className="text-[20px] font-semibold break-words min-w-0">
                          {detailTitle}
                        </h1>
                        {currentLifecycleStatus === "draft" && (
                          <button
                            onClick={() => {
                              setEditingNameId(selectedId);
                              setEditingNameValue(detailTitle);
                            }}
                            className="p-1 mt-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                            title="Rename collection"
                          >
                            <Pencil className="size-4" />
                          </button>
                        )}
                      </div>
                    )}
                  <div className="flex-shrink-0 mt-1.5">
                    {statusBadge(currentLifecycleStatus)}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between">
                  {activeMapping ? (
                    <p className="text-sm font-medium text-muted-foreground min-w-0 flex-1 break-words">
                      {sourceCatalogTitle}
                      <ArrowRight className="inline size-3.5 mx-1 align-middle" />
                      {targetCatalogTitle}
                      {entryCount > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 text-[11px] font-bold align-middle whitespace-nowrap">
                          {entryCount} Mapping{entryCount !== 1 ? "s" : ""}
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm font-medium text-muted-foreground min-w-0 flex-1 break-words">
                      Link source and target catalogs to start mapping controls
                    </p>
                  )}
                  <div
                    className={`flex items-center justify-end gap-2 flex-shrink-0 flex-wrap ${
                      editingNameId === selectedId
                        ? "pointer-events-none opacity-50"
                        : ""
                    }`}
                    title={
                      editingNameId === selectedId
                        ? "Save or cancel the title edit first"
                        : undefined
                    }
                  >
                  {/* Lifecycle transition buttons */}
                  {currentActions.map((action) => {
                    const button = (
                      <button
                        key={action.target}
                        onClick={() =>
                          openTransitionModal(
                            action.target,
                            action.label,
                            action.requireComments,
                          )
                        }
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-xs font-medium transition-colors shadow-md ${action.color}`}
                      >
                        {action.icon}
                        {action.label}
                      </button>
                    );

                    // Determine required permission based on action label
                    if (["Approve", "Reject"].includes(action.label)) {
                      return (
                        <RBACAction
                          key={action.target}
                          module="control_mapping"
                          action="approve"
                        >
                          {button}
                        </RBACAction>
                      );
                    }
                    if (action.label === "Publish") {
                      return (
                        <RBACAction
                          key={action.target}
                          module="control_mapping"
                          action="publish"
                        >
                          {button}
                        </RBACAction>
                      );
                    }
                    if (action.label === "Submit for Review") {
                      return (
                        <RBACAction
                          key={action.target}
                          module="control_mapping"
                          action="update"
                        >
                          {button}
                        </RBACAction>
                      );
                    }
                    if (["Supersede", "Withdraw"].includes(action.label)) {
                      return (
                        <RBACAction
                          key={action.target}
                          module="control_mapping"
                          action="update"
                        >
                          {button}
                        </RBACAction>
                      );
                    }
                    return button;
                  })}

                  {activeMapping && (
                    <button
                      onClick={handleLoadCoverage}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                    >
                      <BarChart3 className="size-3.5" /> Coverage Report
                    </button>
                  )}
                  {/* Edit toggle — only shows "Edit", View is inside the mapping panel */}
                  {activeMapping && isEditable && !editMode && (
                    <button
                      onClick={() => setEditMode(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                    >
                      <Pencil className="size-3.5" /> Edit
                    </button>
                  )}
                  </div>
                </div>
              </div>
            </div>

            {/* Derived-collection notice */}
            {detail?.derived && (
              <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/25 text-sm text-indigo-700 dark:text-indigo-300">
                <Sparkles className="size-4 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold">
                    Derived mapping collection
                  </p>
                  {(detail.derivedFromCollectionATitle ||
                    detail.derivedFromCollectionBTitle) && (
                    <p className="text-xs mt-0.5 break-words">
                      Derived from:{" "}
                      {[
                        detail.derivedFromCollectionATitle,
                        detail.derivedFromCollectionBTitle,
                      ]
                        .filter(Boolean)
                        .join(" + ")}
                    </p>
                  )}
                  <p className="text-xs mt-0.5 text-indigo-600/80 dark:text-indigo-300/70">
                    Entries in this collection are generated automatically and
                    are read-only.
                  </p>
                </div>
              </div>
            )}

            {/* Chain-mapping suggestions banner — hidden in the UI per request.
                Remove the `false &&` below to show it again. */}
            {false &&
              !detail?.derived &&
              !dismissedSuggestions &&
              autoSuggestions.length > 0 && (
                <div className="relative overflow-hidden rounded-2xl border border-violet-200/70 dark:border-violet-500/30 bg-gradient-to-br from-violet-50 via-indigo-50 to-fuchsia-50 dark:from-violet-500/10 dark:via-indigo-500/[0.07] dark:to-fuchsia-500/10 shadow-sm">
                  {/* Decorative glow */}
                  <div className="pointer-events-none absolute -top-16 -right-16 size-44 rounded-full bg-violet-400/20 dark:bg-violet-500/20 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-20 -left-10 size-44 rounded-full bg-indigo-400/15 dark:bg-indigo-500/15 blur-3xl" />

                  {/* Header */}
                  <div className="relative flex items-center gap-3 px-4 py-3.5 border-b border-violet-200/50 dark:border-violet-500/20">
                    <div className="relative flex-shrink-0">
                      <div className="size-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                        <Sparkles className="size-5 text-white" />
                      </div>
                      <span className="absolute inset-0 rounded-xl bg-violet-500/40 blur-md -z-10 animate-pulse" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-violet-800 dark:text-violet-200">
                        Chain mapping available
                      </p>
                      <p className="text-[11px] text-violet-600/80 dark:text-violet-300/70 mt-0.5">
                        {autoSuggestions.length} derived mapping
                        {autoSuggestions.length !== 1 ? "s" : ""} can be
                        generated from a shared catalog
                      </p>
                    </div>
                    <RBACAction module="control_mapping" action="create">
                      <button
                        onClick={handleApplyAllSuggestions}
                        disabled={applyingAll}
                        className="group inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-xs font-semibold transition-all shadow-md shadow-violet-500/25 hover:-translate-y-px disabled:opacity-50 disabled:translate-y-0 disabled:cursor-not-allowed flex-shrink-0 whitespace-nowrap"
                      >
                        {applyingAll ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="size-3.5 group-hover:scale-110 transition-transform" />
                        )}
                        {applyingAll
                          ? "Creating..."
                          : "Create All Derived Mappings"}
                      </button>
                    </RBACAction>
                    <button
                      onClick={() => setDismissedSuggestions(true)}
                      className="flex-shrink-0 p-1 rounded-lg text-violet-500 hover:text-violet-700 hover:bg-violet-500/10 dark:hover:text-violet-300 transition-colors"
                      title="Dismiss"
                    >
                      <X className="size-4" />
                    </button>
                  </div>

                  {/* Suggestions */}
                  <div className="relative p-3 space-y-2.5">
                    {autoSuggestions.map((s) => {
                      const key = `${s.collectionAId}:${s.collectionBId}`;
                      return (
                        <div
                          key={key}
                          className="rounded-xl border border-violet-200/60 dark:border-violet-500/20 bg-white/70 dark:bg-white/[0.03] backdrop-blur-sm px-3 py-3 space-y-2"
                        >
                          {/* Derived mapping title */}
                          <div className="flex items-start gap-2">
                            <GitMerge className="size-3.5 mt-0.5 flex-shrink-0 text-violet-500 dark:text-violet-400" />
                            <p className="text-[13px] font-semibold text-violet-800 dark:text-violet-200 break-words min-w-0">
                              {s.derivedMappingTitle ??
                                `${s.collectionATitle} → ${s.collectionBTitle}`}
                            </p>
                          </div>

                          {/* Chain visual: source catalog -> target catalog */}
                          <div className="flex items-center gap-2 flex-wrap pl-6">
                            <span
                              className="inline-flex items-center gap-1.5 max-w-full px-2.5 py-1 rounded-lg bg-violet-100 dark:bg-violet-500/15 border border-violet-200 dark:border-violet-500/25 text-violet-700 dark:text-violet-300 text-[11px] font-semibold min-w-0"
                              title={
                                s.derivedSourceCatalogTitle ?? s.collectionATitle
                              }
                            >
                              <span className="truncate">
                                {s.derivedSourceCatalogTitle ??
                                  s.collectionATitle}
                              </span>
                            </span>

                            <span className="inline-flex items-center gap-1 text-violet-400 dark:text-violet-500 flex-shrink-0">
                              <span className="hidden sm:block w-4 border-t border-dashed border-violet-300 dark:border-violet-500/40" />
                              <Link2 className="size-3.5" />
                              <span className="hidden sm:block w-4 border-t border-dashed border-violet-300 dark:border-violet-500/40" />
                            </span>

                            <span
                              className="inline-flex items-center gap-1.5 max-w-full px-2.5 py-1 rounded-lg bg-indigo-100 dark:bg-indigo-500/15 border border-indigo-200 dark:border-indigo-500/25 text-indigo-700 dark:text-indigo-300 text-[11px] font-semibold min-w-0"
                              title={
                                s.derivedTargetCatalogTitle ?? s.collectionBTitle
                              }
                            >
                              <span className="truncate">
                                {s.derivedTargetCatalogTitle ??
                                  s.collectionBTitle}
                              </span>
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Rejected notice */}
            {currentLifecycleStatus === "rejected" && (
              <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
                <XCircle className="size-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">
                    This mapping collection was rejected
                  </p>
                  <p className="text-xs mt-0.5">
                    You can edit the mappings and submit for review again.
                  </p>
                </div>
              </div>
            )}

            {/* ── READ-ONLY VIEW (default) ── */}
            {!editMode && activeMapping && (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-stretch">
                  <div className="rounded-lg border border-border bg-card px-4 py-3 flex flex-col">
                    <p className="text-xs font-medium text-[#475569] dark:text-white">
                      Source Catalog
                    </p>
                    <p className="text-sm font-semibold mt-0.5 break-words">
                      {sourceCatalogTitle}
                    </p>
                    <p className="text-xs text-muted-foreground mt-auto pt-1">
                      {coverage?.totalSourceControls ?? sourceControls.length}{" "}
                      controls
                    </p>
                  </div>
                  <div className="rounded-lg border border-border bg-card px-4 py-3 flex flex-col">
                    <p className="text-xs font-medium text-[#475569] dark:text-white">
                      Target Catalog
                    </p>
                    <p className="text-sm font-semibold mt-0.5 break-words">
                      {targetCatalogTitle}
                    </p>
                    <p className="text-xs text-muted-foreground mt-auto pt-1">
                      {coverage?.totalTargetControls ?? targetControls.length}{" "}
                      controls
                    </p>
                  </div>
                  <div className="rounded-lg border border-border bg-card px-4 py-3">
                    {(() => {
                      const totalSource = sourceControls.length;
                      const totalTarget = targetControls.length;
                      const mappedSourceCount = mappedSourceIds.size;
                      const mappedTargetCount = mappedTargetIds.size;

                      const srcPercent = coverage
                        ? coverage.sourceCoveragePercent
                        : totalSource > 0
                          ? (mappedSourceCount / totalSource) * 100
                          : 0;
                      const tgtPercent = coverage
                        ? coverage.targetCoveragePercent
                        : totalTarget > 0
                          ? (mappedTargetCount / totalTarget) * 100
                          : 0;
                      const srcMappedLabel = coverage
                        ? `${coverage.mappedSourceControls} / ${coverage.totalSourceControls} mapped`
                        : `${mappedSourceCount} / ${totalSource} mapped`;
                      const tgtCoveredLabel = coverage
                        ? `${coverage.coveredTargetControls} / ${coverage.totalTargetControls} covered`
                        : `${mappedTargetCount} / ${totalTarget} covered`;

                      return (
                        <div className="grid grid-cols-2 gap-3 h-full">
                          <div>
                            <p className="text-xs font-medium text-[#475569] dark:text-white">
                              Source Coverage
                            </p>
                            <p className="text-sm font-semibold mt-0.5 text-indigo-600 dark:text-violet-400">
                              {srcPercent.toFixed(1)}%
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {srcMappedLabel}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-[#475569] dark:text-white">
                              Target Coverage
                            </p>
                            <p className="text-sm font-semibold mt-0.5 text-emerald-600 dark:text-emerald-400">
                              {tgtPercent.toFixed(1)}%
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {tgtCoveredLabel}
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Mapping entries */}
                {entryCount > 0 ? (
                  (() => {
                    // Group entries by sourceKey for the "Source View" tab
                    const sourceGroups: Record<
                      string,
                      typeof normalizedEntries
                    > = {};
                    const sourceOrder: string[] = [];
                    for (const entry of normalizedEntries) {
                      if (!sourceGroups[entry.sourceKey]) {
                        sourceGroups[entry.sourceKey] = [];
                        sourceOrder.push(entry.sourceKey);
                      }
                      sourceGroups[entry.sourceKey].push(entry);
                    }
                    const activeTab =
                      (expandedRelatedEntries[
                        "__tab__"
                      ] as unknown as string) ?? "grouped";
                    const setTab = (tab: string) => {
                      setExpandedRelatedEntries((prev) => ({
                        ...prev,
                        ["__tab__"]: tab as unknown as boolean,
                      }));
                      setMappingPage(1);
                    };

                    // Search + pagination for All tab
                    const searchLower = mappingSearch.toLowerCase();
                    const filteredEntries = searchLower
                      ? normalizedEntries.filter((entry) => {
                          const srcCtrl = sourceControls.find(
                            (c) =>
                              c.controlId?.toLowerCase() ===
                              entry.sourceKey?.toLowerCase(),
                          );
                          const tgtCtrl = targetControls.find(
                            (c) =>
                              c.controlId?.toLowerCase() ===
                              entry.targetKey?.toLowerCase(),
                          );
                          return (
                            entry.sourceKey
                              ?.toLowerCase()
                              .includes(searchLower) ||
                            entry.targetKey
                              ?.toLowerCase()
                              .includes(searchLower) ||
                            srcCtrl?.title
                              ?.toLowerCase()
                              .includes(searchLower) ||
                            tgtCtrl?.title
                              ?.toLowerCase()
                              .includes(searchLower) ||
                            relLabel(entry.relationship)
                              .toLowerCase()
                              .includes(searchLower)
                          );
                        })
                      : normalizedEntries;
                    const totalPages = Math.max(
                      1,
                      Math.ceil(filteredEntries.length / mappingPageSize),
                    );
                    const pagedEntries = filteredEntries.slice(
                      (mappingPage - 1) * mappingPageSize,
                      mappingPage * mappingPageSize,
                    );

                    // Search for grouped tab
                    const filteredSourceOrder = searchLower
                      ? sourceOrder.filter((srcKey) => {
                          const entries = sourceGroups[srcKey];
                          const srcCtrl = sourceControls.find(
                            (c) =>
                              c.controlId?.toLowerCase() ===
                              srcKey.toLowerCase(),
                          );
                          if (
                            srcKey.toLowerCase().includes(searchLower) ||
                            srcCtrl?.title?.toLowerCase().includes(searchLower)
                          )
                            return true;
                          return entries.some((entry) => {
                            const tgtCtrl = targetControls.find(
                              (c) =>
                                c.controlId?.toLowerCase() ===
                                entry.targetKey?.toLowerCase(),
                            );
                            return (
                              entry.targetKey
                                ?.toLowerCase()
                                .includes(searchLower) ||
                              tgtCtrl?.title
                                ?.toLowerCase()
                                .includes(searchLower) ||
                              relLabel(entry.relationship)
                                .toLowerCase()
                                .includes(searchLower)
                            );
                          });
                        })
                      : sourceOrder;

                    // Pagination for grouped (Source View) tab
                    const sourceTotalPages = Math.max(
                      1,
                      Math.ceil(
                        filteredSourceOrder.length / mappingPageSize,
                      ),
                    );
                    const pagedSourceOrder = filteredSourceOrder.slice(
                      (mappingPage - 1) * mappingPageSize,
                      mappingPage * mappingPageSize,
                    );

                    return (
                      <div className="rounded-2xl border border-border/60 bg-card overflow-hidden shadow-sm">
                        {/* Card header */}
                        <div className="px-6 py-4 border-b border-border/60 bg-gradient-to-r from-indigo-500/[0.06] via-violet-500/[0.04] to-transparent dark:from-indigo-500/[0.08] dark:via-violet-500/[0.05] dark:to-transparent">
                          <div className="flex items-center gap-3">
                            <div className="size-8 rounded-lg bg-indigo-500/15 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                              <GitMerge className="size-4 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-foreground">
                                Control Mappings
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                All mapped control relationships
                              </p>
                            </div>
                            <div className="ml-auto flex items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 text-[11px] font-bold">
                                {normalizedEntries.length}{" "}
                                {normalizedEntries.length === 1
                                  ? "Mapping"
                                  : "Mappings"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Relationship legend */}
                        <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b border-border/40">
                          <span className="text-xs font-medium text-[#475569] dark:text-white mr-1">
                            Relationship Legend
                          </span>
                          {RELATIONSHIPS.map((r) => (
                            <span
                              key={r.value}
                              className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full ${r.color}`}
                            >
                              <span className="size-1.5 rounded-full bg-current opacity-60" />
                              {r.label}
                            </span>
                          ))}
                        </div>

                        {/* Tabs — just 2: Source View | All */}
                        <div className="flex items-center gap-0 px-4 pt-3 pb-0 border-b border-border/40">
                          {(["grouped", "all"] as const).map((tab) => {
                            const isActive = activeTab === tab;
                            return (
                              <button
                                key={tab}
                                onClick={() => setTab(tab)}
                                className={`inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold border-b-2 whitespace-nowrap transition-colors ${
                                  isActive
                                    ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                                    : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}
                              >
                                {tab === "all" ? "All" : "Source View"}
                                {tab === "all" && (
                                  <span
                                    className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${isActive ? "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" : "bg-muted text-muted-foreground"}`}
                                  >
                                    {normalizedEntries.length}
                                  </span>
                                )}
                                {tab === "grouped" && (
                                  <span
                                    className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${isActive ? "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" : "bg-muted text-muted-foreground"}`}
                                  >
                                    {sourceOrder.length}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        {/* Search + rows-per-page toolbar */}
                        <div className="flex items-center gap-3 px-5 py-3 border-b border-border/40 bg-white/[0.01]">
                          <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                            <input
                              type="text"
                              placeholder="Search controls"
                              value={mappingSearch}
                              onChange={(e) => {
                                setMappingSearch(e.target.value);
                                setMappingPage(1);
                              }}
                              className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-white/[0.05] border border-border/50 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-colors"
                            />
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            {activeTab === "grouped" &&
                              (() => {
                                const expandDisabled =
                                  filteredSourceOrder.length > 0 &&
                                  filteredSourceOrder.every(
                                    (srcKey) => expandedSources[srcKey] === true,
                                  );
                                const collapseDisabled =
                                  !filteredSourceOrder.some(
                                    (srcKey) => expandedSources[srcKey] === true,
                                  );
                                return (
                                  <>
                                    <button
                                      onClick={() => {
                                        const newExpanded: Record<string, boolean> =
                                          {};
                                        filteredSourceOrder.forEach((srcKey) => {
                                          newExpanded[srcKey] = true;
                                        });
                                        setExpandedSources(newExpanded);
                                      }}
                                      disabled={expandDisabled}
                                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground transition-colors ${
                                        expandDisabled
                                          ? "opacity-30 cursor-not-allowed pointer-events-none"
                                          : "hover:bg-accent"
                                      }`}
                                    >
                                      <ChevronDown className="size-3" /> Expand All
                                    </button>
                                    <button
                                      onClick={() => setExpandedSources({})}
                                      disabled={collapseDisabled}
                                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-foreground transition-colors ${
                                        collapseDisabled
                                          ? "opacity-30 cursor-not-allowed pointer-events-none"
                                          : "hover:bg-accent"
                                      }`}
                                    >
                                      <ChevronRight className="size-3" /> Collapse All
                                    </button>
                                  </>
                                );
                              })()}
                            <span className="text-xs font-medium text-[#475569] dark:text-white/70 whitespace-nowrap">
                              Rows per page
                            </span>
                            <Dropdown
                              value={String(mappingPageSize)}
                              onChange={(v) => {
                                setMappingPageSize(Number(v));
                                setMappingPage(1);
                              }}
                              options={[10, 25, 50, 100].map((n) => ({
                                value: String(n),
                                label: String(n),
                              }))}
                              size="sm"
                              align="end"
                              className="!min-w-[52px] !h-8 !text-xs !px-2.5"
                            />
                          </div>
                        </div>

                        {/* ── TAB: All (original flat view) ── */}
                        {activeTab === "all" && (
                          <DataTable
                            columns={[
                              {
                                key: "source",
                                header: "Source Control",
                                render: (entry: any) => {
                                  const title =
                                    entry.sourceControlTitle ??
                                    sourceControls.find(
                                      (c) =>
                                        c.controlId?.toLowerCase() ===
                                        entry.sourceKey?.toLowerCase(),
                                    )?.title;
                                  return (
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <span className="font-mono text-[11px] font-medium text-foreground flex-shrink-0">
                                        {entry.sourceKey?.toUpperCase()}
                                      </span>
                                      {title && (
                                        <span className="text-xs text-muted-foreground truncate">
                                          {title}
                                        </span>
                                      )}
                                    </div>
                                  );
                                },
                                align: "left",
                              },
                              {
                                key: "relationship",
                                header: "Relationship",
                                render: (entry: any) => (
                                  <span
                                    className={`inline-flex items-center justify-center gap-1.5 text-[10px] font-bold py-1.5 px-2 rounded-full ${relColor(entry.relationship)}`}
                                  >
                                    <ArrowRight className="size-3 opacity-60 flex-shrink-0" />
                                    {relLabel(entry.relationship)}
                                  </span>
                                ),
                                align: "center",
                              },
                              {
                                key: "target",
                                header: "Target Control",
                                render: (entry: any) => {
                                  const title =
                                    entry.targetControlTitle ??
                                    targetControls.find(
                                      (c) =>
                                        c.controlId?.toLowerCase() ===
                                        entry.targetKey?.toLowerCase(),
                                    )?.title;
                                  return (
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <span className="font-mono text-[11px] font-medium text-foreground flex-shrink-0">
                                        {entry.targetKey?.toUpperCase()}
                                      </span>
                                      {title && (
                                        <span className="text-xs text-muted-foreground truncate">
                                          {title}
                                        </span>
                                      )}
                                    </div>
                                  );
                                },
                                align: "left",
                              },
                            ] as Column<any>[]}
                            data={pagedEntries}
                            page={mappingPage - 1}
                            pageSize={mappingPageSize}
                            totalItems={filteredEntries.length}
                            onPageChange={(page) => setMappingPage(page + 1)}
                            itemLabel="mappings"
                            emptyText="No results found"
                            keyField="id"
                          />
                        )}

                        {/* ── TAB: Source View (1 source → N targets) ── */}
                        {activeTab === "grouped" && (
                          <div className="divide-y divide-border/40">
                            {filteredSourceOrder.length === 0 && (
                              <div className="py-12 text-center text-xs text-muted-foreground">
                                No results found
                              </div>
                            )}
                            {pagedSourceOrder.map((srcKey) => {
                              const entries = sourceGroups[srcKey];
                              const srcCtrl = sourceControls.find(
                                (c) =>
                                  c.controlId?.toLowerCase() ===
                                  srcKey.toLowerCase(),
                              );
                              const isExpanded =
                                expandedSources[srcKey] === true;
                              return (
                                <div key={srcKey} className="p-5">
                                  {/* Source header */}
                                  <button
                                    onClick={() =>
                                      setExpandedSources((prev) => ({
                                        ...prev,
                                        [srcKey]: !isExpanded,
                                      }))
                                    }
                                    className="w-full flex items-center justify-start gap-3 mb-3 text-left hover:opacity-80 transition-opacity background-none border-none p-0"
                                  >
                                    <ChevronRight
                                      className={`size-4 text-muted-foreground flex-shrink-0 transition-transform ${
                                        isExpanded ? "rotate-90" : ""
                                      }`}
                                    />
                                    <span className="font-mono text-[13px] font-semibold text-foreground flex-shrink-0">
                                      {srcKey?.toUpperCase()}
                                    </span>
                                    {(entries[0]?.sourceControlTitle ??
                                      srcCtrl?.title) && (
                                      <span className="text-[14px] font-medium text-foreground min-w-0 flex-1 break-words">
                                        {entries[0]?.sourceControlTitle ??
                                          srcCtrl?.title}
                                      </span>
                                    )}
                                    <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-[11px] font-bold text-violet-400">
                                      {entries.length} Mapping
                                      {entries.length !== 1 ? "s" : ""}
                                    </span>
                                  </button>
                                  {/* Collapsed preview — target chips inline */}
                                  {!isExpanded && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedSources((prev) => ({
                                          ...prev,
                                          [srcKey]: true,
                                        }))
                                      }
                                      className="flex flex-wrap items-center gap-1.5 ml-7 text-left"
                                    >
                                      {entries
                                        .slice(0, 6)
                                        .map((entry) => (
                                          <span
                                            key={entry.id}
                                            className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${relColor(entry.relationship)}`}
                                            title={`${relLabel(entry.relationship)} → ${entry.targetKey}`}
                                          >
                                            <span className="size-1.5 rounded-full bg-current opacity-60 flex-shrink-0" />
                                            {entry.targetKey?.toUpperCase()}
                                            {entry.derived && (
                                              <Sparkles className="size-2.5" />
                                            )}
                                          </span>
                                        ))}
                                      {entries.length > 6 && (
                                        <span className="text-[10px] font-semibold text-muted-foreground px-1.5 hover:text-foreground transition-colors">
                                          +{entries.length - 6} more
                                        </span>
                                      )}
                                    </button>
                                  )}
                                  {/* Target list */}
                                  {isExpanded && (
                                    <div className="ml-4 pl-4 border-l-2 border-indigo-500/20 space-y-2">
                                      {entries.map((entry) => {
                                        const targetTitle =
                                          entry.targetControlTitle ??
                                          targetControls.find(
                                            (c) =>
                                              c.controlId?.toLowerCase() ===
                                              entry.targetKey?.toLowerCase(),
                                          )?.title;
                                        return (
                                          <div
                                            key={entry.id}
                                            className="flex items-center gap-3 py-2 px-3 rounded-xl bg-white/[0.02] border border-border/30 hover:bg-white/[0.04] transition-colors"
                                          >
                                            <span
                                              className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0 ${relColor(entry.relationship)}`}
                                            >
                                              <ArrowRight className="size-3 opacity-60" />
                                              {relLabel(entry.relationship)}
                                            </span>
                                            <span className="font-mono text-[11px] font-semibold text-foreground flex-shrink-0">
                                              {entry.targetKey?.toUpperCase()}
                                            </span>
                                            {targetTitle && (
                                              <span className="text-xs text-foreground">
                                                {targetTitle}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            {/* Pagination footer — Source View */}
                            {filteredSourceOrder.length > 0 && (
                              <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015]">
                                <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                                  Showing{" "}
                                  <span className="font-semibold text-[#334155] dark:text-white/90">
                                    {(mappingPage - 1) * mappingPageSize + 1}–
                                    {Math.min(
                                      mappingPage * mappingPageSize,
                                      filteredSourceOrder.length,
                                    )}
                                  </span>{" "}
                                  of{" "}
                                  <span className="font-semibold text-[#334155] dark:text-white/90">
                                    {filteredSourceOrder.length}
                                  </span>{" "}
                                  source controls
                                </p>

                                {sourceTotalPages > 1 && (
                                  <div className="flex items-center gap-0.5">
                                    <button
                                      disabled={mappingPage === 1}
                                      onClick={() =>
                                        setMappingPage(mappingPage - 1)
                                      }
                                      className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                      <ChevronLeft className="size-3" />
                                    </button>
                                    {Array.from(
                                      { length: sourceTotalPages },
                                      (_, i) => i + 1,
                                    ).map((p) => (
                                      <button
                                        key={p}
                                        onClick={() => setMappingPage(p)}
                                        className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${
                                          p === mappingPage
                                            ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                                            : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                                        }`}
                                      >
                                        {p}
                                      </button>
                                    ))}
                                    <button
                                      disabled={mappingPage >= sourceTotalPages}
                                      onClick={() =>
                                        setMappingPage(mappingPage + 1)
                                      }
                                      className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                      <ChevronRight className="size-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <div className="rounded-2xl border border-border/60 bg-card p-12 text-center">
                    <div className="size-14 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mx-auto mb-4">
                      <GitMerge className="size-7 text-indigo-400" />
                    </div>
                    <p className="text-sm font-semibold text-foreground">
                      No mappings yet
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5 mb-5 max-w-xs mx-auto">
                      Switch to edit mode to start mapping controls between
                      catalogs
                    </p>
                    {isEditable && (
                      <button
                        onClick={() => setEditMode(true)}
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-indigo-600 dark:bg-violet-600 text-white text-sm font-semibold hover:bg-indigo-700 dark:hover:bg-violet-700 transition-all shadow-lg shadow-indigo-500/20 hover:-translate-y-px"
                      >
                        <Pencil className="size-3.5" /> Start Mapping
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── EDIT MODE — Catalog selector (when no mapping yet) ── */}
            {(editMode || !activeMapping) && !activeMapping && (
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="text-sm font-semibold mb-4">
                  Link Source & Target Catalogs
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">
                      Source Catalog
                    </label>
                    <Dropdown
                      variant="form"
                      placeholder="Select source catalog"
                      value={sourceCatalogId}
                      onChange={(v) => setSourceCatalogId(v)}
                      options={catalogOptions}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-foreground">
                      Target Catalog
                    </label>
                    <Dropdown
                      variant="form"
                      placeholder="Select target catalog"
                      value={targetCatalogId}
                      onChange={(v) => setTargetCatalogId(v)}
                      options={catalogOptions}
                    />
                  </div>
                  <button
                    onClick={handleLinkCatalogs}
                    disabled={!sourceCatalogId || !targetCatalogId}
                    className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-violet-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-violet-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-violet-500/20"
                  >
                    <Link2 className="inline size-4 mr-1.5" /> Link Catalogs
                  </button>
                </div>
              </div>
            )}

            {/* ── EDIT MODE — Three-panel workspace ── */}
            {editMode &&
              activeMapping &&
              (() => {
                const CHIP_SYMBOLS: Record<string, string> = {
                  "equal-to": "≡",
                  "equivalent-to": "≈",
                  "subset-of": "⊂",
                  "superset-of": "⊃",
                  "intersects-with": "∩",
                  "no-relationship": "∅",
                };
                const CHIP_COLORS: Record<string, string> = {
                  "equal-to":
                    "bg-emerald-500/15 text-emerald-400 border border-emerald-500/40",
                  "equivalent-to":
                    "bg-blue-500/15 text-blue-400 border border-blue-500/40",
                  "subset-of":
                    "bg-amber-500/15 text-amber-400 border border-amber-500/40",
                  "superset-of":
                    "bg-purple-500/15 text-purple-400 border border-purple-500/40",
                  "intersects-with":
                    "bg-cyan-500/15 text-cyan-400 border border-cyan-500/40",
                  "no-relationship":
                    "bg-slate-500/15 text-slate-400 border border-slate-500/40",
                };
                const SHOW_CHIPS = 2;
                const hasSelection =
                  selectedSources.length > 0 || selectedTargets.length > 0;

                return (
                  <div
                    ref={workspaceRef}
                    className="grid grid-cols-1 lg:grid-cols-[1fr_360px_1fr] gap-0 rounded-xl overflow-hidden border border-[#e2e8f0] dark:border-[#252a3d] bg-[#f8fafc] dark:bg-[#13162a]"
                    style={{ minHeight: 580 }}
                  >
                    {/* ══ LEFT: Source Controls ══ */}
                    <div className="flex flex-col min-w-0 border-b lg:border-b-0 lg:border-r border-[#e2e8f0] dark:border-[#252a3d] bg-[#f1f5f9] dark:bg-[#161929]">
                      {/* Header */}
                      <div className="px-4 py-3 flex items-center justify-between border-b border-[#e2e8f0] dark:border-[#252a3d] bg-[#f8fafc] dark:bg-[#13162a]">
                        <div className="flex items-center gap-2">
                          <span className="size-2 rounded-full bg-violet-500 shadow-[0_0_6px_rgba(139,92,246,0.6)]" />
                          <span className="text-xs font-medium text-violet-400">
                            Source
                          </span>
                        </div>
                        <span
                          className="text-[9px] font-bold px-2 py-0.5 rounded-full text-violet-300"
                          style={{
                            background: "rgba(139,92,246,0.12)",
                            border: "1px solid rgba(139,92,246,0.2)",
                          }}
                        >
                          {filteredSource.length} controls
                        </span>
                      </div>
                      <div className="px-3 py-1.5 border-b border-[#e2e8f0] dark:border-[#1e2236]">
                        <p className="text-[11px] font-semibold text-slate-400 dark:text-white/50 truncate">
                          {sourceCatalogTitle}
                        </p>
                      </div>
                      {/* Search */}
                      <div className="px-3 py-2 border-b border-[#e2e8f0] dark:border-[#252a3d]">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-[#4a5270]" />
                          <input
                            type="text"
                            placeholder="Filter controls"
                            value={sourceSearch}
                            onChange={(e) => setSourceSearch(e.target.value)}
                            className="w-full pl-7 pr-3 py-1.5 text-[11px] rounded-md outline-none placeholder:text-[#4a5270] text-[#0f172a] dark:text-white/70 bg-white dark:bg-[#0e1020] border border-[#e2e8f0] dark:border-[#252a3d]"
                          />
                        </div>
                      </div>
                      {/* List */}
                      <div
                        className="flex-1 overflow-y-auto"
                        style={{ maxHeight: 500 }}
                      >
                        {sourceLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <Loader2 className="size-4 animate-spin text-violet-400" />
                          </div>
                        ) : filteredSource.length === 0 ? (
                          <div className="py-8 text-center text-xs text-[#4a5270]">
                            No controls found
                          </div>
                        ) : (
                          filteredSource.map((ctrl) => {
                            const isSelected = selectedSources.includes(
                              ctrl.controlId,
                            );
                            const ctrlMappings = normalizedEntries.filter(
                              (e) =>
                                e.sourceKey?.toLowerCase() ===
                                ctrl.controlId?.toLowerCase(),
                            );
                            const isMapped = ctrlMappings.length > 0;
                            const visibleChips = ctrlMappings.slice(
                              0,
                              SHOW_CHIPS,
                            );
                            const extraCount = ctrlMappings.length - SHOW_CHIPS;
                            const isMtMBlocked =
                              !isSelected &&
                              selectedSources.length >= 1 &&
                              selectedTargets.length > 1;
                            return (
                              <Tooltip
                                key={ctrl.controlId}
                                open={isMtMBlocked ? undefined : false}
                              >
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    ref={(el) => {
                                      if (el)
                                        sourceRefs.current.set(
                                          ctrl.controlId,
                                          el as unknown as HTMLDivElement,
                                        );
                                    }}
                                    onClick={() => {
                                      if (isMtMBlocked) return;
                                      setSelectedSources((prev) => {
                                        if (prev.includes(ctrl.controlId)) {
                                          return prev.filter(
                                            (id) => id !== ctrl.controlId,
                                          );
                                        }
                                        if (
                                          prev.length >= 1 &&
                                          selectedTargets.length > 1
                                        ) {
                                          toast.warning(
                                            "Map your targets to a single source.",
                                          );
                                          return prev;
                                        }
                                        return [...prev, ctrl.controlId];
                                      });
                                    }}
                                    className={`w-full text-left flex items-center gap-2 px-3 py-2 transition-all border-b border-[#e2e8f0] dark:border-[#1e2236] ${isMtMBlocked ? "opacity-40 cursor-not-allowed" : isSelected ? "" : "hover:bg-indigo-50 dark:hover:bg-indigo-500/[0.07]"}`}
                                    style={{
                                      background: isSelected
                                        ? "rgba(16,185,129,0.08)"
                                        : undefined,
                                      borderLeft: isSelected
                                        ? "2px solid #10b981"
                                        : "2px solid transparent",
                                    }}
                                  >
                                    <span
                                      className={`size-1.5 rounded-full flex-shrink-0 transition-colors ${isSelected ? "bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.6)]" : isMapped ? "bg-emerald-500" : "bg-[#cbd5e1] dark:bg-[#2d3348]"}`}
                                    />
                                    <span
                                      className={`font-mono text-[11px] font-bold flex-shrink-0 transition-colors ${isSelected ? "text-emerald-600 dark:text-emerald-400" : "text-indigo-600 dark:text-[#6b7fc4]"}`}
                                    >
                                      {ctrl.controlId?.toUpperCase()}
                                    </span>
                                    <span
                                      className={`text-[11px] flex-1 min-w-0 truncate transition-colors ${isSelected ? "text-[#0f172a] dark:text-white/90" : "text-[#475569] dark:text-white/40"}`}
                                    >
                                      {ctrl.title}
                                    </span>
                                    {visibleChips.length > 0 && (
                                      <div className="flex items-center gap-0.5 flex-shrink-0">
                                        {visibleChips.map((m) => (
                                          <Tooltip key={m.id}>
                                            <TooltipTrigger asChild>
                                              <span
                                                className={`text-[10px] font-bold px-2 py-0.5 rounded cursor-default whitespace-nowrap ${CHIP_COLORS[m.relationship] ?? "bg-slate-500/15 text-slate-400 border border-slate-500/40"}`}
                                              >
                                                {
                                                  relLabel(
                                                    m.relationship,
                                                  ).split(" ")[0]
                                                }
                                                -{m.targetKey}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="top"
                                              className="p-0 overflow-hidden rounded-lg border-0"
                                              style={{
                                                background: "transparent",
                                                boxShadow: "none",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  borderRadius: 10,
                                                  boxShadow:
                                                    "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
                                                }}
                                                className="px-3 py-2.5 min-w-[160px] bg-white dark:bg-[#0d1017] border border-[#e2e8f0] dark:border-[#252a3d]"
                                              >
                                                <p
                                                  className="text-[9px] font-bold tracking-widest mb-1.5"
                                                  style={{ color: "#4a5270" }}
                                                >
                                                  Mapped To
                                                </p>
                                                <div className="flex items-center gap-2">
                                                  <span className="font-mono text-[12px] font-black text-cyan-300">
                                                    {m.targetKey}
                                                  </span>
                                                </div>
                                                <div className="mt-1.5 flex items-center gap-1.5">
                                                  <span
                                                    className={`text-[9px] font-bold px-2 py-0.5 rounded ${CHIP_COLORS[m.relationship] ?? "bg-slate-500/15 text-slate-400 border border-slate-500/40"}`}
                                                  >
                                                    {relLabel(m.relationship)}
                                                  </span>
                                                </div>
                                              </div>
                                            </TooltipContent>
                                          </Tooltip>
                                        ))}
                                        {extraCount > 0 && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span
                                                className="text-[8px] font-bold px-1 py-px rounded cursor-default text-slate-500 dark:text-white/40"
                                                style={{
                                                  background:
                                                    "rgba(100,116,139,0.12)",
                                                  border:
                                                    "1px solid rgba(100,116,139,0.25)",
                                                }}
                                              >
                                                +{extraCount}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="top"
                                              className="p-0 overflow-hidden rounded-lg border-0"
                                              style={{
                                                background: "transparent",
                                                boxShadow: "none",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  borderRadius: 10,
                                                  boxShadow:
                                                    "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
                                                }}
                                                className="px-3 py-2.5 min-w-[160px] bg-white dark:bg-[#0d1017] border border-[#e2e8f0] dark:border-[#252a3d]"
                                              >
                                                <p
                                                  className="text-[9px] font-bold tracking-widest mb-1.5"
                                                  style={{ color: "#4a5270" }}
                                                >
                                                  +{extraCount} More Mappings
                                                </p>
                                                <div className="space-y-1">
                                                  {ctrlMappings
                                                    .slice(SHOW_CHIPS)
                                                    .map((m2) => (
                                                      <div
                                                        key={m2.id}
                                                        className="flex items-center gap-2"
                                                      >
                                                        <span className="font-mono text-[11px] font-black text-cyan-600 dark:text-cyan-300">
                                                          {m2.targetKey}
                                                        </span>
                                                        <span
                                                          className={`text-[9px] font-bold px-2 py-0.5 rounded ${CHIP_COLORS[m2.relationship] ?? "bg-slate-500/15 text-slate-400 border border-slate-500/40"}`}
                                                        >
                                                          {
                                                            relLabel(
                                                              m2.relationship,
                                                            ).split(" ")[0]
                                                          }
                                                        </span>
                                                      </div>
                                                    ))}
                                                </div>
                                              </div>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                      </div>
                                    )}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  className="p-0 overflow-hidden rounded-lg border-0"
                                  style={{
                                    background: "transparent",
                                    boxShadow: "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      borderRadius: 10,
                                      boxShadow:
                                        "0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)",
                                    }}
                                    className="px-3 py-2.5 max-w-[240px] bg-white dark:bg-[#0d1017] border border-[#e2e8f0] dark:border-[#252a3d]"
                                  >
                                    <p className="text-[9px] font-bold tracking-widest mb-1 text-amber-600 dark:text-amber-400">
                                      One source at a time
                                    </p>
                                    <p className="text-[11px] leading-snug text-[#475569] dark:text-white/70">
                                      Multiple targets are selected. Pick just
                                      one source.
                                    </p>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* ══ CENTER: Mapping Panel ══ */}
                    <div className="relative flex flex-col border-b lg:border-b-0 lg:border-r border-[#e2e8f0] dark:border-[#252a3d] bg-[#f8fafc] dark:bg-[#13162a]">
                      {/* Header */}
                      <div className="px-4 py-3 flex items-center justify-between border-b border-[#e2e8f0] dark:border-[#252a3d] bg-white dark:bg-[#0e1020]">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-medium text-[#94a3b8]">
                              {hasSelection ? "Mapping" : "Mapping Layer"}
                            </p>
                            {!hasSelection && entryCount > 0 && (
                              <span
                                className="text-[9px] font-bold px-2 py-0.5 rounded-full text-emerald-300"
                                style={{
                                  background: "rgba(16,185,129,0.1)",
                                  border: "1px solid rgba(16,185,129,0.2)",
                                }}
                              >
                                {entryCount} saved
                              </span>
                            )}
                          </div>
                          {hasSelection && (
                            <p className="text-[10px] text-[#4a5270] mt-0.5">
                              {selectedSources.length} source ·{" "}
                              {selectedTargets.length} target selected
                            </p>
                          )}
                        </div>
                        {/* View + Delete buttons in edit mode */}
                        {editMode && (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => {
                                setDeleteMode((v) => !v);
                                setMarkedForDelete([]);
                              }}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors"
                              style={
                                deleteMode
                                  ? {
                                      background: "rgba(239,68,68,0.15)",
                                      border: "1px solid rgba(239,68,68,0.35)",
                                      color: "#f87171",
                                    }
                                  : {
                                      background: "rgba(239,68,68,0.08)",
                                      border: "1px solid rgba(239,68,68,0.2)",
                                      color: "#f87171",
                                    }
                              }
                            >
                              <Trash2 className="size-3" /> Delete
                            </button>
                            <button
                              onClick={() => setEditMode(false)}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors"
                              style={{
                                background: "rgba(139,92,246,0.12)",
                                border: "1px solid rgba(139,92,246,0.25)",
                                color: "#a78bfa",
                              }}
                            >
                              <Eye className="size-3" /> View
                            </button>
                          </div>
                        )}
                      </div>

                      <div
                        className="flex-1 overflow-y-auto"
                        style={{ maxHeight: 400 }}
                      >
                        {/* Delete mode: show all entries with delete toggles */}
                        {deleteMode ? (
                          <div className="p-4 space-y-2">
                            <p className="text-[10px] text-[#4a5270] mb-3">
                              Check entries to mark for deletion, then click
                              Save.
                            </p>
                            {normalizedEntries.length === 0 ? (
                              <p className="text-[11px] text-[#4a5270] text-center py-8">
                                No entries to delete
                              </p>
                            ) : (
                              normalizedEntries.map((entry) => {
                                const isMarked = markedForDelete.includes(
                                  entry.id,
                                );
                                const isDerived = !!(entry as any).derived;
                                return (
                                  <button
                                    key={entry.id}
                                    type="button"
                                    disabled={isDerived}
                                    title={
                                      isDerived
                                        ? "Derived entry — read-only, cannot be deleted"
                                        : undefined
                                    }
                                    onClick={() =>
                                      setMarkedForDelete((p) =>
                                        isMarked
                                          ? p.filter((x) => x !== entry.id)
                                          : [...p, entry.id],
                                      )
                                    }
                                    className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 border transition-all text-left ${
                                      isDerived
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                    style={{
                                      background: isMarked
                                        ? "rgba(239,68,68,0.08)"
                                        : "rgba(255,255,255,0.02)",
                                      borderColor: isMarked
                                        ? "rgba(239,68,68,0.3)"
                                        : "#252a3d",
                                    }}
                                  >
                                    {/* Checkbox */}
                                    <span
                                      className="flex-shrink-0 size-3.5 rounded border transition-all flex items-center justify-center"
                                      style={{
                                        background: isMarked
                                          ? "#ef4444"
                                          : "transparent",
                                        borderColor: isMarked
                                          ? "#ef4444"
                                          : "#4a5270",
                                      }}
                                    >
                                      {isMarked && (
                                        <svg
                                          className="size-2.5 text-white"
                                          viewBox="0 0 10 10"
                                          fill="none"
                                        >
                                          <path
                                            d="M2 5l2.5 2.5L8 3"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      )}
                                    </span>
                                    {/* Content */}
                                    <span className="flex items-center gap-2 flex-1 justify-center">
                                      <span className="font-mono text-[10px] font-semibold text-[#6b7fc4]">
                                        {entry.sourceKey?.toUpperCase()}
                                      </span>
                                      <span
                                        className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${CHIP_COLORS[entry.relationship] ?? "bg-slate-500/15 text-slate-400"}`}
                                      >
                                        {relLabel(entry.relationship)}
                                      </span>
                                      <span className="font-mono text-[10px] font-semibold text-[#6b7fc4]">
                                        {entry.targetKey?.toUpperCase()}
                                      </span>
                                      {isDerived && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-400 text-[9px] font-bold">
                                          <Sparkles className="size-2.5" /> Derived
                                        </span>
                                      )}
                                    </span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        ) : !hasSelection ? (
                          <div className="flex flex-col justify-center h-full px-5 py-8 gap-3">
                            {[
                              {
                                step: "1",
                                color: "#8b5cf6",
                                glow: "rgba(139,92,246,0.25)",
                                label: "Pick a Source",
                                sub: "Click any control in the Source panel on the left",
                              },
                              {
                                step: "2",
                                color: "#06b6d4",
                                glow: "rgba(6,182,212,0.25)",
                                label: "Pick a Target",
                                sub: "Click any control in the Target panel on the right",
                              },
                              {
                                step: "3",
                                color: "#10b981",
                                glow: "rgba(16,185,129,0.25)",
                                label: "Set Relationship & Save",
                                sub: "Choose a relationship type then hit Save",
                              },
                            ].map((s, i) => (
                              <div key={s.step}>
                                <div className="flex items-start gap-3">
                                  <div
                                    className="size-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-black"
                                    style={{
                                      background: `${s.color}22`,
                                      border: `1px solid ${s.color}55`,
                                      color: s.color,
                                      boxShadow: `0 0 8px ${s.glow}`,
                                    }}
                                  >
                                    {s.step}
                                  </div>
                                  <div>
                                    <p
                                      className="text-[11px] font-semibold"
                                      style={{ color: s.color }}
                                    >
                                      {s.label}
                                    </p>
                                    <p className="text-[10px] text-[#4a5270] mt-0.5 leading-relaxed">
                                      {s.sub}
                                    </p>
                                  </div>
                                </div>
                                {i < 2 && (
                                  <div className="ml-3 mt-1 w-px h-3 bg-gradient-to-b from-[#e2e8f0] dark:from-[#252a3d] to-transparent" />
                                )}
                              </div>
                            ))}
                            <div
                              className="mt-2 rounded-lg px-3 py-2.5 text-center border border-dashed border-[#e2e8f0] dark:border-[#252a3d]"
                              style={{ background: "rgba(99,102,241,0.05)" }}
                            >
                              <p className="text-[10px] text-[#4a5270]">
                                Select{" "}
                                <span className="text-[#0f172a] dark:text-white/60 font-bold">
                                  multiple sources
                                </span>{" "}
                                and{" "}
                                <span className="text-[#0f172a] dark:text-white/60 font-bold">
                                  multiple targets
                                </span>{" "}
                                at once
                              </p>
                            </div>
                          </div>
                        ) : (
                          /* Selection active: mapping builder */
                          <div className="p-4 space-y-4">
                            {/* Source cards */}
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="size-1.5 rounded-full bg-violet-400" />
                                <p className="text-[10px] font-bold tracking-widest text-violet-400">
                                  Source Controls ({selectedSources.length})
                                </p>
                              </div>
                              {selectedSources.length === 0 ? (
                                <div className="rounded-lg px-3 py-2.5 text-[11px] text-[#4a5270] text-center border border-dashed border-[#e2e8f0] dark:border-[#252a3d]">
                                  Click a source control to add
                                </div>
                              ) : (
                                <div className="space-y-1.5">
                                  {selectedSources.map((id) => {
                                    const ctrl = sourceControls.find(
                                      (c) => c.controlId === id,
                                    );
                                    return (
                                      <div
                                        key={id}
                                        className="flex items-center gap-2 rounded-lg px-3 py-2 bg-[#e2e8f0] dark:bg-[#1a1e35] border border-[#cbd5e1] dark:border-[#2a3258]"
                                      >
                                        <span className="font-mono text-[11px] font-bold text-violet-600 dark:text-violet-400 flex-shrink-0">
                                          {id}
                                        </span>
                                        <span className="text-[11px] text-[#475569] dark:text-white/55 truncate flex-1">
                                          {ctrl?.title}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setSelectedSources((p) =>
                                              p.filter((x) => x !== id),
                                            )
                                          }
                                          className="text-[#4a5270] hover:text-red-400 transition-colors flex-shrink-0"
                                        >
                                          <X className="size-3" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Arrow divider */}
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[#e2e8f0] dark:to-[#252a3d]" />
                              <div className="size-6 rounded-full flex items-center justify-center flex-shrink-0 bg-[#e2e8f0] dark:bg-[#1a1e35] border border-[#e2e8f0] dark:border-[#252a3d]">
                                <svg
                                  className="size-3 text-[#4a5270]"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                                  />
                                </svg>
                              </div>
                              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[#e2e8f0] dark:to-[#252a3d]" />
                            </div>

                            {/* Target cards */}
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="size-1.5 rounded-full bg-cyan-400" />
                                <p className="text-[10px] font-bold tracking-widest text-cyan-400">
                                  Target Controls ({selectedTargets.length})
                                </p>
                              </div>
                              {selectedTargets.length === 0 ? (
                                <div className="rounded-lg px-3 py-2.5 text-[11px] text-[#4a5270] text-center border border-dashed border-[#e2e8f0] dark:border-[#252a3d]">
                                  Click a target control to add
                                </div>
                              ) : (
                                <div className="space-y-1.5">
                                  {selectedTargets.map((id) => {
                                    const ctrl = targetControls.find(
                                      (c) => c.controlId === id,
                                    );
                                    return (
                                      <div
                                        key={id}
                                        className="flex items-center gap-2 rounded-lg px-3 py-2 bg-[#e2e8f0] dark:bg-[#1a1e35] border border-[#cbd5e1] dark:border-[#1e3548]"
                                      >
                                        <span className="font-mono text-[11px] font-bold text-cyan-600 dark:text-cyan-400 flex-shrink-0">
                                          {id}
                                        </span>
                                        <span className="text-[11px] text-[#475569] dark:text-white/55 truncate flex-1">
                                          {ctrl?.title}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setSelectedTargets((p) =>
                                              p.filter((x) => x !== id),
                                            )
                                          }
                                          className="text-[#4a5270] hover:text-red-400 transition-colors flex-shrink-0"
                                        >
                                          <X className="size-3" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Relationship type */}
                            <div>
                              <p className="text-[10px] font-bold tracking-widest text-[#64748b] mb-2">
                                Relationship Type
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {(
                                  [
                                    {
                                      value: "equivalent-to",
                                      label: "Equivalent To",
                                      active: {
                                        background: "rgba(59,130,246,0.2)",
                                        color: "#60a5fa",
                                        border:
                                          "1px solid rgba(59,130,246,0.4)",
                                        boxShadow:
                                          "0 0 10px rgba(59,130,246,0.2)",
                                      },
                                    },
                                    {
                                      value: "equal-to",
                                      label: "Equal To",
                                      active: {
                                        background: "rgba(16,185,129,0.2)",
                                        color: "#34d399",
                                        border:
                                          "1px solid rgba(16,185,129,0.4)",
                                        boxShadow:
                                          "0 0 10px rgba(16,185,129,0.2)",
                                      },
                                    },
                                    {
                                      value: "subset-of",
                                      label: "Subset Of",
                                      active: {
                                        background: "rgba(245,158,11,0.2)",
                                        color: "#fbbf24",
                                        border:
                                          "1px solid rgba(245,158,11,0.4)",
                                        boxShadow:
                                          "0 0 10px rgba(245,158,11,0.2)",
                                      },
                                    },
                                    {
                                      value: "superset-of",
                                      label: "Superset Of",
                                      active: {
                                        background: "rgba(168,85,247,0.2)",
                                        color: "#c084fc",
                                        border:
                                          "1px solid rgba(168,85,247,0.4)",
                                        boxShadow:
                                          "0 0 10px rgba(168,85,247,0.2)",
                                      },
                                    },
                                    {
                                      value: "intersects-with",
                                      label: "Intersects With",
                                      active: {
                                        background: "rgba(6,182,212,0.2)",
                                        color: "#22d3ee",
                                        border: "1px solid rgba(6,182,212,0.4)",
                                        boxShadow:
                                          "0 0 10px rgba(6,182,212,0.2)",
                                      },
                                    },
                                    {
                                      value: "no-relationship",
                                      label: "No Relationship",
                                      active: {
                                        background: "rgba(148,163,184,0.15)",
                                        color: "#94a3b8",
                                        border:
                                          "1px solid rgba(148,163,184,0.35)",
                                        boxShadow: "none",
                                      },
                                    },
                                  ] as const
                                ).map((r) => (
                                  <button
                                    key={r.value}
                                    type="button"
                                    onClick={() =>
                                      setSelectedRelType(
                                        selectedRelType === r.value
                                          ? ""
                                          : r.value,
                                      )
                                    }
                                    style={
                                      selectedRelType === r.value
                                        ? r.active
                                        : undefined
                                    }
                                    className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-all ${selectedRelType === r.value ? "" : "bg-[#e2e8f0] dark:bg-[#1a1e35] text-[#475569] dark:text-[#4a5270] border border-[#cbd5e1] dark:border-[#252a3d] hover:bg-[#cbd5e1] dark:hover:bg-[#252a3d]"}`}
                                  >
                                    {r.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="px-4 py-3 mt-auto border-t border-[#e2e8f0] dark:border-[#252a3d] bg-white dark:bg-[#0e1020]">
                        {deleteMode ? (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setDeleteMode(false);
                                setMarkedForDelete([]);
                              }}
                              className="flex-1 py-2 rounded-lg text-xs font-bold text-[#64748b] transition-colors bg-[#e2e8f0] dark:bg-[#1a1e35] border border-[#e2e8f0] dark:border-[#252a3d]"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleDeleteMarked}
                              disabled={
                                deletingEntries || markedForDelete.length === 0
                              }
                              className="flex-1 py-2 rounded-lg text-xs font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                              style={{
                                background:
                                  markedForDelete.length > 0
                                    ? "linear-gradient(135deg, #ef4444, #dc2626)"
                                    : undefined,
                                boxShadow:
                                  markedForDelete.length > 0
                                    ? "0 0 16px rgba(239,68,68,0.3)"
                                    : "none",
                              }}
                            >
                              {deletingEntries ? (
                                <Loader2 className="size-3 animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="size-3" /> Delete{" "}
                                  {markedForDelete.length > 0
                                    ? `(${markedForDelete.length})`
                                    : ""}
                                </>
                              )}
                            </button>
                          </div>
                        ) : hasSelection ? (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedSources([]);
                                setSelectedTargets([]);
                                setSelectedRelType("");
                              }}
                              className="flex-1 py-2 rounded-lg text-xs font-bold text-[#64748b] hover:text-[#0f172a] dark:hover:text-white transition-colors bg-[#e2e8f0] dark:bg-[#1a1e35] border border-[#e2e8f0] dark:border-[#252a3d]"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveAll}
                              disabled={
                                savingEntry ||
                                selectedSources.length === 0 ||
                                selectedTargets.length === 0 ||
                                !selectedRelType
                              }
                              className="flex-1 py-2 rounded-lg text-xs font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                              style={{
                                background:
                                  "linear-gradient(135deg, #10b981, #059669)",
                                boxShadow:
                                  savingEntry ||
                                  selectedSources.length === 0 ||
                                  selectedTargets.length === 0 ||
                                  !selectedRelType
                                    ? "none"
                                    : "0 0 16px rgba(16,185,129,0.3)",
                              }}
                            >
                              {savingEntry ? (
                                <Loader2 className="size-3 animate-spin mx-auto" />
                              ) : (
                                "Save"
                              )}
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {RELATIONSHIPS.map((r) => (
                              <span
                                key={r.value}
                                className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${r.color}`}
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ══ RIGHT: Target Controls ══ */}
                    <div className="flex flex-col min-w-0 bg-[#f1f5f9] dark:bg-[#161929]">
                      {/* Header */}
                      <div className="px-4 py-3 flex items-center justify-between border-b border-[#e2e8f0] dark:border-[#252a3d] bg-[#f8fafc] dark:bg-[#13162a]">
                        <div className="flex items-center gap-2">
                          <span className="size-2 rounded-full bg-cyan-500 shadow-[0_0_6px_rgba(6,182,212,0.6)]" />
                          <span className="text-xs font-medium text-cyan-400">
                            Target
                          </span>
                        </div>
                        <span
                          className="text-[9px] font-bold px-2 py-0.5 rounded-full text-cyan-300"
                          style={{
                            background: "rgba(6,182,212,0.12)",
                            border: "1px solid rgba(6,182,212,0.2)",
                          }}
                        >
                          {filteredTarget.length} controls
                        </span>
                      </div>
                      <div className="px-4 py-1.5 border-b border-[#e2e8f0] dark:border-[#1e2236]">
                        <p className="text-[11px] font-semibold text-slate-500 dark:text-white/50 truncate">
                          {targetCatalogTitle}
                        </p>
                      </div>
                      {/* Search */}
                      <div className="px-3 py-2 border-b border-[#e2e8f0] dark:border-[#252a3d]">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3 text-[#4a5270]" />
                          <input
                            type="text"
                            placeholder="Filter controls"
                            value={targetSearch}
                            onChange={(e) => setTargetSearch(e.target.value)}
                            className="w-full pl-7 pr-3 py-1.5 text-[11px] rounded-md outline-none placeholder:text-[#4a5270] text-[#0f172a] dark:text-white/70 bg-white dark:bg-[#0e1020] border border-[#e2e8f0] dark:border-[#252a3d]"
                          />
                        </div>
                      </div>
                      {/* List */}
                      <div
                        className="flex-1 overflow-y-auto"
                        style={{ maxHeight: 500 }}
                      >
                        {targetLoading ? (
                          <div className="flex items-center justify-center py-10">
                            <Loader2 className="size-4 animate-spin text-cyan-400" />
                          </div>
                        ) : filteredTarget.length === 0 ? (
                          <div className="py-8 text-center text-xs text-[#4a5270]">
                            No controls found
                          </div>
                        ) : (
                          filteredTarget.map((ctrl) => {
                            const isSelected = selectedTargets.includes(
                              ctrl.controlId,
                            );
                            const isDropActive = dropTarget === ctrl.controlId;
                            const ctrlMappings = normalizedEntries.filter(
                              (e) =>
                                e.targetKey?.toLowerCase() ===
                                ctrl.controlId?.toLowerCase(),
                            );
                            const isMapped = ctrlMappings.length > 0;
                            const visibleChips = ctrlMappings.slice(
                              0,
                              SHOW_CHIPS,
                            );
                            const extraCount = ctrlMappings.length - SHOW_CHIPS;
                            const isMtMBlocked =
                              !isSelected &&
                              selectedTargets.length >= 1 &&
                              selectedSources.length > 1;
                            return (
                              <Tooltip
                                key={ctrl.controlId}
                                open={isMtMBlocked ? undefined : false}
                              >
                                <TooltipTrigger asChild>
                                  <div
                                    ref={(el) => {
                                      if (el)
                                        targetRefs.current.set(
                                          ctrl.controlId,
                                          el,
                                        );
                                    }}
                                    onDragOver={(e) =>
                                      handleDragOver(e, ctrl.controlId)
                                    }
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) =>
                                      handleDrop(e, ctrl.controlId)
                                    }
                                    onClick={() => {
                                      if (isMtMBlocked) return;
                                      setSelectedTargets((prev) => {
                                        if (prev.includes(ctrl.controlId)) {
                                          return prev.filter(
                                            (id) => id !== ctrl.controlId,
                                          );
                                        }
                                        if (
                                          prev.length >= 1 &&
                                          selectedSources.length > 1
                                        ) {
                                          toast.warning(
                                            "Map your sources to a single target.",
                                          );
                                          return prev;
                                        }
                                        return [...prev, ctrl.controlId];
                                      });
                                    }}
                                    className={`flex items-center gap-2 px-3 py-2 transition-all border-b border-[#e2e8f0] dark:border-[#1e2236] ${isMtMBlocked ? "opacity-40 cursor-not-allowed" : `cursor-pointer ${!isSelected && !isDropActive ? "hover:bg-cyan-50 dark:hover:bg-cyan-500/[0.07]" : ""}`}`}
                                    style={{
                                      background: isDropActive
                                        ? "rgba(139,92,246,0.08)"
                                        : isSelected
                                          ? "rgba(16,185,129,0.08)"
                                          : undefined,
                                      borderLeft: isDropActive
                                        ? "2px solid #8b5cf6"
                                        : isSelected
                                          ? "2px solid #10b981"
                                          : "2px solid transparent",
                                      outline: isDropActive
                                        ? "1px solid rgba(139,92,246,0.3)"
                                        : "none",
                                    }}
                                  >
                                    <span
                                      className={`size-1.5 rounded-full flex-shrink-0 transition-colors ${isSelected ? "bg-emerald-400 shadow-[0_0_5px_rgba(52,211,153,0.6)]" : isMapped ? "bg-emerald-500" : "bg-[#cbd5e1] dark:bg-[#2d3348]"}`}
                                    />
                                    <span
                                      className={`font-mono text-[11px] font-bold flex-shrink-0 transition-colors ${isSelected ? "text-emerald-600 dark:text-emerald-400" : "text-cyan-700 dark:text-[#4e8fa8]"}`}
                                    >
                                      {ctrl.controlId?.toUpperCase()}
                                    </span>
                                    <span
                                      className={`text-[11px] flex-1 min-w-0 truncate transition-colors ${isSelected ? "text-[#0f172a] dark:text-white/90" : "text-[#475569] dark:text-white/40"}`}
                                    >
                                      {ctrl.title}
                                    </span>
                                    {visibleChips.length > 0 && (
                                      <div className="flex items-center gap-0.5 flex-shrink-0">
                                        {visibleChips.map((m) => (
                                          <Tooltip key={m.id}>
                                            <TooltipTrigger asChild>
                                              <span
                                                className={`text-[10px] font-bold px-2 py-0.5 rounded cursor-default whitespace-nowrap ${CHIP_COLORS[m.relationship] ?? "bg-slate-500/15 text-slate-400 border border-slate-500/40"}`}
                                              >
                                                {
                                                  relLabel(
                                                    m.relationship,
                                                  ).split(" ")[0]
                                                }
                                                -{m.sourceKey}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="top"
                                              className="p-0 overflow-hidden rounded-lg border-0"
                                              style={{
                                                background: "transparent",
                                                boxShadow: "none",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  borderRadius: 10,
                                                  boxShadow:
                                                    "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
                                                }}
                                                className="px-3 py-2.5 min-w-[160px] bg-white dark:bg-[#0d1017] border border-[#e2e8f0] dark:border-[#252a3d]"
                                              >
                                                <p
                                                  className="text-[9px] font-bold tracking-widest mb-1.5"
                                                  style={{ color: "#4a5270" }}
                                                >
                                                  Mapped From
                                                </p>
                                                <div className="flex items-center gap-2">
                                                  <span className="font-mono text-[12px] font-black text-violet-300">
                                                    {m.sourceKey}
                                                  </span>
                                                </div>
                                                <div className="mt-1.5 flex items-center gap-1.5">
                                                  <span
                                                    className={`text-[9px] font-bold px-2 py-0.5 rounded ${CHIP_COLORS[m.relationship] ?? "bg-slate-500/15 text-slate-400 border border-slate-500/40"}`}
                                                  >
                                                    {relLabel(m.relationship)}
                                                  </span>
                                                </div>
                                              </div>
                                            </TooltipContent>
                                          </Tooltip>
                                        ))}
                                        {extraCount > 0 && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span
                                                className="text-[8px] font-bold px-1 py-px rounded cursor-default text-slate-500 dark:text-white/40"
                                                style={{
                                                  background:
                                                    "rgba(100,116,139,0.12)",
                                                  border:
                                                    "1px solid rgba(100,116,139,0.25)",
                                                }}
                                              >
                                                +{extraCount}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="top"
                                              className="p-0 overflow-hidden rounded-lg border-0"
                                              style={{
                                                background: "transparent",
                                                boxShadow: "none",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  borderRadius: 10,
                                                  boxShadow:
                                                    "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
                                                }}
                                                className="px-3 py-2.5 min-w-[160px] bg-white dark:bg-[#0d1017] border border-[#e2e8f0] dark:border-[#252a3d]"
                                              >
                                                <p
                                                  className="text-[9px] font-bold tracking-widest mb-1.5"
                                                  style={{ color: "#4a5270" }}
                                                >
                                                  +{extraCount} More Mappings
                                                </p>
                                                <div className="space-y-1">
                                                  {ctrlMappings
                                                    .slice(SHOW_CHIPS)
                                                    .map((m2) => (
                                                      <div
                                                        key={m2.id}
                                                        className="flex items-center gap-2"
                                                      >
                                                        <span className="font-mono text-[11px] font-black text-violet-600 dark:text-violet-300">
                                                          {m2.sourceKey}
                                                        </span>
                                                        <span
                                                          className={`text-[9px] font-bold px-2 py-0.5 rounded ${CHIP_COLORS[m2.relationship] ?? "bg-slate-500/15 text-slate-400 border border-slate-500/40"}`}
                                                        >
                                                          {
                                                            relLabel(
                                                              m2.relationship,
                                                            ).split(" ")[0]
                                                          }
                                                        </span>
                                                      </div>
                                                    ))}
                                                </div>
                                              </div>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="left"
                                  className="p-0 overflow-hidden rounded-lg border-0"
                                  style={{
                                    background: "transparent",
                                    boxShadow: "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      borderRadius: 10,
                                      boxShadow:
                                        "0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)",
                                    }}
                                    className="px-3 py-2.5 max-w-[240px] bg-white dark:bg-[#0d1017] border border-[#e2e8f0] dark:border-[#252a3d]"
                                  >
                                    <p className="text-[9px] font-bold tracking-widest mb-1 text-amber-600 dark:text-amber-400">
                                      One target at a time
                                    </p>
                                    <p className="text-[11px] leading-snug text-[#475569] dark:text-white/70">
                                      Multiple sources are selected. Pick just
                                      one target.
                                    </p>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
          </>
        )}

        {/* ── Relationship Picker Modal ── */}
        <Modal
          open={showRelPicker}
          onClose={() => {
            setShowRelPicker(false);
            setEditingEntryId(null);
          }}
          title={editingEntryId ? "Update Relationship" : "Select Relationship"}
          description={`${pendingSource} → ${pendingTarget}`}
          size="sm"
        >
          <div className="space-y-2">
            {RELATIONSHIPS.map((r) => (
              <button
                key={r.value}
                onClick={() =>
                  editingEntryId
                    ? handleUpdateEntry(r.value)
                    : handleCreateEntry(r.value)
                }
                disabled={savingEntry || !!updatingEntryId}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-border hover:border-indigo-300 dark:hover:border-violet-500/30 hover:bg-indigo-50/30 dark:hover:bg-violet-500/[0.04] transition-all text-left ${
                  savingEntry || updatingEntryId ? "opacity-40" : ""
                }`}
              >
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.color}`}
                >
                  {r.label}
                </span>
              </button>
            ))}
          </div>
          {savingEntry && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="size-4 animate-spin text-indigo-500" />
            </div>
          )}
        </Modal>

        {/* ── Transition Modal ── */}
        <Modal
          open={showTransition}
          onClose={() => setShowTransition(false)}
          title={transitionLabel}
          size="sm"
          footer={
            <ModalFooterButtons
              onCancel={() => setShowTransition(false)}
              onSubmit={handleTransition}
              submitLabel={
                transitioning
                  ? "Processing"
                  : transitionLabel === "Submit for Review"
                    ? "Submit for Review"
                    : `Submit for ${transitionLabel}`
              }
              submitDisabled={
                transitioning ||
                (transitionRequireComments && !transitionComments.trim())
              }
            />
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              You are about to change the status of{" "}
              <span className="font-semibold text-foreground">
                "{detail?.title ?? selectedRow?.title ?? "Mapping Collection"}"
              </span>{" "}
              to{" "}
              <span className="font-semibold text-foreground capitalize">
                {transitionTarget.replace(/_/g, " ")}
              </span>
              .
            </p>

            {lifecycleActions[currentLifecycleStatus]?.find(
              (a) => a.target === transitionTarget,
            )?.warning && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-sm text-amber-700 dark:text-amber-400">
                <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
                <span className="text-xs">
                  {
                    lifecycleActions[currentLifecycleStatus]?.find(
                      (a) => a.target === transitionTarget,
                    )?.warning
                  }
                </span>
              </div>
            )}

            {!detail?.derived && autoSuggestions.length > 0 && (
              <div className="relative overflow-hidden flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-500/10 dark:to-indigo-500/10 border border-violet-200 dark:border-violet-500/25">
                <div className="size-7 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-violet-500/25">
                  <Sparkles className="size-3.5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                    Auto-merge available
                  </p>
                  <p className="text-[11px] text-violet-600/80 dark:text-violet-300/70 mt-0.5 leading-relaxed">
                    {autoSuggestions.length} derived mapping
                    {autoSuggestions.length !== 1 ? "s" : ""} can be generated
                    for this collection. You can create{" "}
                    {autoSuggestions.length !== 1 ? "them" : "it"} from the
                    "Chain mapping available" banner before continuing.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <MessageSquare className="size-3.5" />
                Comments{" "}
                {transitionRequireComments ? (
                  <span className="text-red-500">*</span>
                ) : (
                  <span className="text-muted-foreground text-xs">
                    (optional)
                  </span>
                )}
              </label>
              <textarea
                placeholder={
                  transitionRequireComments
                    ? "Please provide a reason"
                    : "Add a comment"
                }
                value={transitionComments}
                onChange={(e) => setTransitionComments(e.target.value)}
                rows={3}
                className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-violet-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 resize-none leading-relaxed text-[#0f172a] dark:text-white"
              />
            </div>
          </div>
        </Modal>

        {/* ── Coverage Modal ── */}
        <Modal
          open={showCoverage}
          onClose={() => {
            setShowCoverage(false);
            setUnmappedPage(1);
            setUncoveredPage(1);
            setGapsPage(1);
          }}
          title="Coverage & Gap Analysis"
          size="xl"
        >
          {coverageLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="size-6 animate-spin text-indigo-500" />
            </div>
          ) : coverage ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="rounded-xl border border-border bg-card p-4 md:col-span-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground mb-2">
                        Source Coverage
                      </p>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-3xl font-bold text-foreground whitespace-nowrap">
                          {coverage.sourceCoveragePercent.toFixed(1)}%
                        </span>
                        <span className="text-xs text-muted-foreground text-left">
                          {coverage.mappedSourceControls} of{" "}
                          {coverage.totalSourceControls} mapped
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 rounded-lg bg-indigo-50 dark:bg-violet-500/10 px-3 py-2 text-right">
                      <p className="text-[10px] tracking-wide text-indigo-700 dark:text-violet-300 whitespace-nowrap">
                        Missing
                      </p>
                      <p className="text-lg font-bold text-indigo-700 dark:text-violet-300">
                        {coverage.totalSourceControls -
                          coverage.mappedSourceControls}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 h-3 bg-[#f1f5f9] dark:bg-white/[0.05] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 dark:bg-violet-500 rounded-full transition-all"
                      style={{
                        width: `${Math.min(coverage.sourceCoveragePercent, 100)}%`,
                      }}
                    />
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Shows how much of the source catalog has already been
                    connected to the target.
                  </p>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 md:col-span-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground mb-2">
                        Target Coverage
                      </p>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-3xl font-bold text-foreground whitespace-nowrap">
                          {coverage.targetCoveragePercent.toFixed(1)}%
                        </span>
                        <span className="text-xs text-muted-foreground text-left">
                          {coverage.coveredTargetControls} of{" "}
                          {coverage.totalTargetControls} covered
                        </span>
                      </div>
                    </div>
                    {coverage.totalTargetControls -
                      coverage.coveredTargetControls ===
                    0 ? (
                      <div className="flex-shrink-0 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 px-3 py-2 text-right flex flex-col items-center gap-0.5">
                        <CheckCircle2 className="size-4 text-emerald-600 dark:text-emerald-400" />
                        <p className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                          Fully Covered
                        </p>
                      </div>
                    ) : (
                      <div className="flex-shrink-0 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-right">
                        <p className="text-[10px] tracking-wide text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                          Remaining
                        </p>
                        <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                          {coverage.totalTargetControls -
                            coverage.coveredTargetControls}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 h-3 bg-[#f1f5f9] dark:bg-white/[0.05] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{
                        width: `${Math.min(coverage.targetCoveragePercent, 100)}%`,
                      }}
                    />
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Shows how much of the target catalog already has at least
                    one incoming mapping.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {coverage.unmappedSourceControlIds.length > 0 && (
                  <div className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-gradient-to-br from-amber-50 to-amber-100/40 dark:from-amber-500/10 dark:to-amber-500/5 p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="text-xs font-bold text-amber-700 dark:text-amber-400">
                          Unmapped Source Controls
                        </p>
                        <p className="text-[11px] text-amber-700/80 dark:text-amber-300/80 mt-0.5">
                          Page {unmappedPage} of{" "}
                          {Math.max(
                            1,
                            Math.ceil(
                              coverage.unmappedSourceControlIds.length /
                                COVERAGE_CHIPS_PER_PAGE,
                            ),
                          )}
                        </p>
                      </div>
                      <span className="rounded-full bg-amber-200/70 dark:bg-amber-500/20 px-2.5 py-1 text-[10px] font-bold text-amber-800 dark:text-amber-300">
                        {coverage.totalSourceControls -
                          coverage.mappedSourceControls}{" "}
                        total
                      </span>
                    </div>
                    <div className="min-h-[98px] rounded-lg border border-amber-200/70 dark:border-amber-500/20 bg-white/40 dark:bg-black/10 p-3">
                      <div className="flex flex-wrap gap-1.5">
                        {pagedUnmappedSourceIds.map((id) => (
                          <span
                            key={id}
                            className="font-mono text-[10px] bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 rounded"
                          >
                            {id}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="min-w-0 text-[11px] text-amber-700/80 dark:text-amber-300/80">
                        Focus on these next to increase source coverage.
                      </p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() =>
                            setUnmappedPage((page) => Math.max(1, page - 1))
                          }
                          disabled={unmappedPage === 1}
                          className="px-2.5 py-1 rounded-md border border-amber-200 dark:border-amber-500/20 text-[11px] font-medium whitespace-nowrap disabled:opacity-40"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() =>
                            setUnmappedPage((page) =>
                              Math.min(
                                Math.max(
                                  1,
                                  Math.ceil(
                                    coverage.unmappedSourceControlIds.length /
                                      COVERAGE_CHIPS_PER_PAGE,
                                  ),
                                ),
                                page + 1,
                              ),
                            )
                          }
                          disabled={
                            unmappedPage >=
                            Math.max(
                              1,
                              Math.ceil(
                                coverage.unmappedSourceControlIds.length /
                                  COVERAGE_CHIPS_PER_PAGE,
                              ),
                            )
                          }
                          className="px-2.5 py-1 rounded-md border border-amber-200 dark:border-amber-500/20 text-[11px] font-medium whitespace-nowrap disabled:opacity-40"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {coverage.uncoveredTargetControlIds.length > 0 && (
                  <div className="rounded-xl border border-rose-200 dark:border-rose-500/30 bg-gradient-to-br from-rose-50 to-rose-100/40 dark:from-rose-500/10 dark:to-rose-500/5 p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="text-xs font-bold text-rose-700 dark:text-rose-400">
                          Uncovered Target Controls
                        </p>
                        <p className="text-[11px] text-rose-700/80 dark:text-rose-300/80 mt-0.5">
                          Page {uncoveredPage} of{" "}
                          {Math.max(
                            1,
                            Math.ceil(
                              coverage.uncoveredTargetControlIds.length /
                                COVERAGE_CHIPS_PER_PAGE,
                            ),
                          )}
                        </p>
                      </div>
                      <span className="rounded-full bg-rose-200/70 dark:bg-rose-500/20 px-2.5 py-1 text-[10px] font-bold text-rose-800 dark:text-rose-300">
                        {coverage.totalTargetControls -
                          coverage.coveredTargetControls}{" "}
                        total
                      </span>
                    </div>
                    <div className="min-h-[98px] rounded-lg border border-rose-200/70 dark:border-rose-500/20 bg-white/40 dark:bg-black/10 p-3">
                      <div className="flex flex-wrap gap-1.5">
                        {pagedUncoveredTargetIds.map((id) => (
                          <span
                            key={id}
                            className="font-mono text-[10px] bg-rose-100 dark:bg-rose-500/20 text-rose-800 dark:text-rose-300 px-1.5 py-0.5 rounded"
                          >
                            {id}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="min-w-0 text-[11px] text-rose-700/80 dark:text-rose-300/80">
                        These target controls still need at least one mapping.
                      </p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() =>
                            setUncoveredPage((page) => Math.max(1, page - 1))
                          }
                          disabled={uncoveredPage === 1}
                          className="px-2.5 py-1 rounded-md border border-rose-200 dark:border-rose-500/20 text-[11px] font-medium whitespace-nowrap disabled:opacity-40"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() =>
                            setUncoveredPage((page) =>
                              Math.min(
                                Math.max(
                                  1,
                                  Math.ceil(
                                    coverage.uncoveredTargetControlIds.length /
                                      COVERAGE_CHIPS_PER_PAGE,
                                  ),
                                ),
                                page + 1,
                              ),
                            )
                          }
                          disabled={
                            uncoveredPage >=
                            Math.max(
                              1,
                              Math.ceil(
                                coverage.uncoveredTargetControlIds.length /
                                  COVERAGE_CHIPS_PER_PAGE,
                              ),
                            )
                          }
                          className="px-2.5 py-1 rounded-md border border-rose-200 dark:border-rose-500/20 text-[11px] font-medium whitespace-nowrap disabled:opacity-40"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {coverage.uncoveredTargetControlIds.length === 0 && (
                  <div className="rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-gradient-to-br from-emerald-50 to-emerald-100/40 dark:from-emerald-500/10 dark:to-emerald-500/5 p-4 flex flex-col items-center justify-center text-center gap-3 min-h-[160px]">
                    <div className="size-12 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="size-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">
                        All Target Controls Covered
                      </p>
                      <p className="text-[11px] text-emerald-700/80 dark:text-emerald-300/80 max-w-xs">
                        All target catalog controls are fully covered. There are
                        no uncovered target catalog controls remaining.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {coverage.gaps.length > 0 && (
                <div className="rounded-xl border border-border overflow-hidden bg-card">
                  <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-[#f8fafc] dark:bg-white/[0.02]">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Gap Details
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Review and work through the highest-volume coverage
                        gaps.
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] tracking-widest text-[#94a3b8]">
                        Showing
                      </p>
                      <p className="text-xs font-semibold">
                        {Math.min(
                          (gapsPage - 1) * COVERAGE_GAPS_PER_PAGE + 1,
                          coverage.gaps.length,
                        )}
                        -
                        {Math.min(
                          gapsPage * COVERAGE_GAPS_PER_PAGE,
                          coverage.gaps.length,
                        )}{" "}
                        of {coverage.gaps.length}
                      </p>
                    </div>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#f5f7fb] dark:bg-white/[0.02] border-b border-border">
                        <th className="px-4 py-2 text-center text-xs font-medium text-[#94a3b8]">
                          Control ID
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-[#94a3b8]">
                          Framework
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-[#94a3b8]">
                          Gap Type
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedGaps.map((gap, i) => (
                        <tr
                          key={`${gap.controlId}-${gap.framework}-${i}`}
                          className="border-b border-[#f1f5f9] dark:border-white/[0.04] last:border-0"
                        >
                          <td className="px-4 py-3 text-center font-mono font-bold">
                            {gap.controlId?.toUpperCase()}
                          </td>
                          <td className="px-4 py-3 text-center capitalize text-muted-foreground">
                            {gap.framework}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                gap.gapType === "unmapped"
                                  ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400"
                                  : "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400"
                              }`}
                            >
                              {gap.gapType}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-[#f8fafc] dark:bg-white/[0.02]">
                    <p className="min-w-0 text-[11px] text-muted-foreground">
                      Use this list as the working queue for improving mapping
                      completeness.
                    </p>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() =>
                          setGapsPage((page) => Math.max(1, page - 1))
                        }
                        disabled={gapsPage === 1}
                        className="px-3 py-1.5 rounded-md border border-border text-[11px] font-medium whitespace-nowrap disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <span className="text-[11px] text-muted-foreground">
                        Page {gapsPage} of{" "}
                        {Math.max(
                          1,
                          Math.ceil(
                            coverage.gaps.length / COVERAGE_GAPS_PER_PAGE,
                          ),
                        )}
                      </span>
                      <button
                        onClick={() =>
                          setGapsPage((page) =>
                            Math.min(
                              Math.max(
                                1,
                                Math.ceil(
                                  coverage.gaps.length / COVERAGE_GAPS_PER_PAGE,
                                ),
                              ),
                              page + 1,
                            ),
                          )
                        }
                        disabled={
                          gapsPage >=
                          Math.max(
                            1,
                            Math.ceil(
                              coverage.gaps.length / COVERAGE_GAPS_PER_PAGE,
                            ),
                          )
                        }
                        className="px-3 py-1.5 rounded-md border border-border text-[11px] font-medium whitespace-nowrap disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-foreground text-center py-6">
              No coverage data available
            </p>
          )}
        </Modal>

        {/* ── Delete Modal ── */}
        <DeleteConfirmDialog
          open={!!deleteId}
          type="Mapping Collection"
          name={collections.find((c) => c.id === deleteId)?.title || ""}
          deleting={deleting}
          onCancel={() => {
            setDeleteId(null);
          }}
          onConfirm={handleDelete}
        />
      </div>
    );
  }

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     LIST VIEW
  ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  if (!selectedId && !mappingIdFromUrl) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1>Control Mappings</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              OSCAL mapping collections map controls across security frameworks
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 self-start sm:self-auto">
            <RBACAction module="control_mapping" action="create">
              <button
                onClick={() => {
                  resetImport();
                  setShowImport(true);
                  // Warm the catalog list so the picker is ready if needed.
                  loadImportCatalogs();
                }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-indigo-500 dark:border-indigo-400 text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-all hover:-translate-y-px"
              >
                <Import className="size-4" /> Import Mapping
              </button>
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
              >
                <Plus className="size-4" /> New Mapping
              </button>
            </RBACAction>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-sm text-red-700 dark:text-red-400">
            <AlertCircle className="size-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600 transition-colors text-xs"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Mapping collections card grid */}
        <EntityCardGrid
          items={paginatedCollections}
          loading={loading}
          getKey={(row) => row.id}
          getStatus={(row) => row.lifecycleStatus}
          onOpen={handleSelectCollection}
          page={listPage - 1}
          pageSize={listPageSize}
          totalItems={filteredCollections.length}
          onPageChange={(page) => setListPage(page + 1)}
          onPageSizeChange={(s) => {
            setListPageSize(s);
            setListPage(1);
          }}
          itemLabel="collections"
          emptyIcon={<GitMerge className="size-8 text-[#cbd5e1] dark:text-white/30" />}
          emptyText="No mapping collections yet"
          toolbarLeft={
            <>
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/50 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search mappings"
                  value={listSearch}
                  onChange={(e) => {
                    setListSearch(e.target.value);
                    setListPage(1);
                  }}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-violet-500/20 focus:border-indigo-500 dark:focus:border-violet-500 transition-all placeholder:text-muted-foreground/50"
                />
              </div>
              <Dropdown
                size="sm"
                variant="pill"
                value={filterStatus}
                onChange={(v) => {
                  setFilterStatus(v);
                  setListPage(1);
                }}
                options={[
                  { value: "", label: "All Status" },
                  { value: "draft", label: "Draft", dot: "bg-slate-400" },
                  { value: "in_review", label: "In Review", dot: "bg-blue-400" },
                  { value: "approved", label: "Approved", dot: "bg-emerald-400" },
                  { value: "published", label: "Published", dot: "bg-purple-400" },
                  { value: "rejected", label: "Rejected", dot: "bg-red-400" },
                  { value: "superseded", label: "Superseded", dot: "bg-orange-400" },
                  { value: "withdrawn", label: "Withdrawn", dot: "bg-rose-400" },
                ]}
              />
              <div className="inline-flex items-center gap-1 p-0.5 rounded-lg bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06]">
                {(
                  [
                    { key: "", label: "All", count: collections.length },
                    {
                      key: "mapped",
                      label: "Mapped",
                      count: collections.filter((c) => !isDerivedCollection(c)).length,
                    },
                    {
                      key: "derived",
                      label: "Derived",
                      count: collections.filter((c) => isDerivedCollection(c)).length,
                    },
                  ] as const
                ).map((t) => {
                  const active = colTypeFilter === t.key;
                  return (
                    <button
                      key={t.key || "all"}
                      type="button"
                      onClick={() => {
                        setColTypeFilter(t.key as "" | "mapped" | "derived");
                        setListPage(1);
                      }}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                        active
                          ? "bg-card text-indigo-600 dark:text-indigo-400 shadow-sm"
                          : "text-[#64748b] dark:text-white/60 hover:text-foreground"
                      }`}
                    >
                      {t.label}
                      <span
                        className={`text-[9px] font-bold tabular-nums px-1 py-0.5 rounded-full ${
                          active
                            ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
                            : "bg-[#e2e8f0] text-[#64748b] dark:bg-white/[0.08] dark:text-white/60"
                        }`}
                      >
                        {t.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          }
          renderTable={(rows) => (
            <DataTable<CollectionRow>
              data={rows}
              rowKey={(row) => row.id}
              onRowClick={(row) => handleSelectCollection(row)}
              emptyText="No mapping collections yet"
              sortKey={colSortKey}
              sortDir={colSortDir}
              onSort={handleColSort}
              sortIcon="arrows"
              columns={[
                {
                  key: "sno",
                  header: "#",
                  align: "center",
                  render: (row) => (
                    <span className="font-medium d">
                      {(colOriginalIndexMap.get(row.id) ?? 0) + 1}
                    </span>
                  ),
                },
                {
                  key: "title",
                  header: "Title",
                  align: "left",
                  headerAlign: "center",
                  sortable: true,
                  ellipsis: 2,
                  tooltip: (row) => row.title,
                  render: (row) => (
                    <div className="flex items-center gap-2 min-w-0">
                      {isDerivedCollection(row) ? (
                        <Waypoints className="size-4 text-violet-600 dark:text-violet-400 flex-shrink-0" />
                      ) : (
                        <Link2 className="size-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                      )}
                      <span className="font-medium text-foreground truncate">
                        {row.title}
                      </span>
                    </div>
                  ),
                },
                {
                  key: "type",
                  header: "Type",
                  align: "center",
                  sortable: true,
                  render: (row) => (
                    <span className="text-xs font-medium text-muted-foreground">
                      {isDerivedCollection(row) ? "Derived" : "Mapped"}
                    </span>
                  ),
                },
                {
                  key: "mappingCount",
                  header: "Mappings",
                  align: "right",
                  sortable: true,
                  render: (row) => (
                    <span className="font-medium text-foreground">
                      {row.mappingCount}
                    </span>
                  ),
                },
                {
                  key: "entryCount",
                  header: "Entries",
                  align: "right",
                  sortable: true,
                  render: (row) => (
                    <span className="font-medium text-foreground">
                      {row.entryCount}
                    </span>
                  ),
                },
                {
                  key: "lifecycleStatus",
                  header: "Status",
                  align: "center",
                  sortable: true,
                  render: (row) => statusBadge(row.lifecycleStatus),
                },
                {
                  key: "createdAt",
                  header: "Created On",
                  align: "center",
                  sortable: true,
                  render: (row) => (
                    <span className="text-xs text-muted-foreground">
                      {row.createdAt}
                    </span>
                  ),
                },
                {
                  key: "updatedAt",
                  header: "Updated On",
                  align: "center",
                  sortable: true,
                  render: (row) => (
                    <span className="text-xs font-medium text-muted-foreground">
                      {row.updatedAt}
                    </span>
                  ),
                },
              ]}
            />
          )}
          renderTitle={(row) => (
            <TitleTooltip text={row.title}>
              <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                {row.title}
              </h3>
            </TitleTooltip>
          )}
          renderHeaderRight={(row) =>
            isDerivedCollection(row) ? (
              <Tip label="Derived">
                <Waypoints className="size-4 text-violet-600 dark:text-violet-400" />
              </Tip>
            ) : (
              <Tip label="Mapped">
                <Link2 className="size-4 text-indigo-600 dark:text-indigo-400" />
              </Tip>
            )
          }
          renderStats={(row) => (
            <StatTiles>
              <StatTile
                icon={<Link2 className="size-3.5" />}
                accent="indigo"
                value={row.mappingCount ?? "—"}
                label="Mappings"
              />
              <StatTile
                icon={<FileText className="size-3.5" />}
                accent="emerald"
                value={row.entryCount ?? "—"}
                label="Entries"
              />
            </StatTiles>
          )}
          renderDates={(row) => (
            <MetaDates created={row.createdAt || "—"} updated={row.updatedAt || "—"} />
          )}
          renderFooterActions={(row) =>
            isDerivedCollection(row) ? null : (
              <CardActions
                deleteModule="control_mapping"
                exportTooltip="Export mapping"
                deleteTooltip="Delete mapping"
                deleteDisabledTooltip="Only draft mappings can be deleted"
                canDelete={
                  hasDeletePermission && row.lifecycleStatus === "draft"
                }
                exporting={exportingId === row.documentId}
                onExport={() => {
                  setExportModalDocId(row.documentId);
                  setExportModalName(row.title);
                }}
                onDelete={() => {
                  window.dispatchEvent(
                    new CustomEvent("delete-collection", { detail: row.id }),
                  );
                }}
              />
            )
          }
        />

        {/* Create Modal */}
        <Modal
          open={showCreate}
          onClose={() => {
            setShowCreate(false);
            setCreateTitle("");
          }}
          title="New Mapping Collection"
          description="Create a new OSCAL mapping collection"
          size="sm"
          footer={
            <ModalFooterButtons
              onCancel={() => {
                setShowCreate(false);
                setCreateTitle("");
              }}
              onSubmit={handleCreate}
              submitLabel={creating ? "Saving" : "Save"}
              submitDisabled={!createTitle.trim() || creating}
            />
          }
        >
          <FormField label="Title" required>
            <FormInput
              placeholder="e.g. NIST 800-53 to ISO 27001 Mapping"
              value={createTitle}
              onChange={setCreateTitle}
            />
          </FormField>
          {accessibleOrgs.length > 0 && (
            <FormField label="Organization">
              <Dropdown
                value={createOrgId}
                onChange={setCreateOrgId}
                placeholder="Select Organization"
                variant="form"
                options={accessibleOrgs.map((org) => ({
                  value: org.id,
                  label: org.name,
                }))}
              />
            </FormField>
          )}
        </Modal>

        {/* Delete Modal (from list) */}
        <DeleteConfirmDialog
          open={!!deleteId && !selectedId}
          type="Mapping Collection"
          name={collections.find((c) => c.id === deleteId)?.title || ""}
          deleting={deleting}
          onCancel={() => {
            setDeleteId(null);
          }}
          onConfirm={handleDelete}
        />

        {/* ── OSCAL Import Pipeline Modal ── */}
        <Modal
          open={showImport}
          onClose={() => {
            setShowImport(false);
            resetImport();
          }}
          title="Import Mapping"
          size="md"
          footer={
            importStep === "select" ? (
              <div className="flex justify-end w-full gap-2">
                <button
                  onClick={() => {
                    setShowImport(false);
                    resetImport();
                  }}
                  className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                {/* Import stages the session, then opens the Commit modal. It's
                    hidden while the file has file-level errors (unknown ids /
                    structural) that require fixing and re-attaching. */}
                {!(mv.blockingErrors.length > 0) && (
                  <button
                    onClick={handleStageImport}
                    disabled={validating || staging || !mv.clean}
                    className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${
                      validating || staging || !mv.clean
                        ? "bg-indigo-400 dark:bg-violet-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-violet-500/10"
                        : "bg-indigo-600 dark:bg-violet-600 hover:bg-indigo-700 dark:hover:bg-violet-700 shadow-indigo-500/20 dark:shadow-violet-500/20"
                    }`}
                  >
                    {staging ? "Importing…" : validating ? "Validating…" : "Import"}
                  </button>
                )}
              </div>
            ) : importStep === "url" ? (
              <div className="flex justify-end w-full gap-2">
                <button
                  onClick={() => {
                    setImportStep("select");
                    setImportUrl("");
                    setImportFile(null);
                    setValidateResult(null);
                    setImportError(null);
                    setValidating(false);
                  }}
                  className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={runImport}
                  disabled={!validateResult || validating}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${
                    !validateResult || validating
                      ? "bg-indigo-400 dark:bg-violet-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-violet-500/10"
                      : "bg-indigo-600 dark:bg-violet-600 hover:bg-indigo-700 dark:hover:bg-violet-700 shadow-indigo-500/20 dark:shadow-violet-500/20"
                  }`}
                >
                  Import
                </button>
              </div>
            ) : importStep === "findings" ? (
              <div className="flex items-center justify-end w-full gap-3">
                <button
                  onClick={() => {
                    setShowImport(false);
                    resetImport();
                  }}
                  className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    resetImport();
                  }}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Try Again
                </button>
              </div>
            ) : null
          }
        >
          {/* Step 0: Select source */}
          {importStep === "select" && (
            <div className="py-6 flex flex-col items-center gap-6">
              <input
                id="mapping-file-input"
                type="file"
                accept=".json,.yaml,.yml,.xml"
                className="hidden"
                // Reset the value on every open so re-selecting the SAME file
                // (e.g. after removing it) still fires onChange.
                onClick={(e) => {
                  (e.target as HTMLInputElement).value = "";
                }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setImportFile(file);
                    setValidateResult(null);
                    setImportError(null);
                    // Reset any prior catalog picks for the new file.
                    setImportSourceCatalogId("");
                    setImportTargetCatalogId("");
                    // Validate immediately (no catalog ids on first pass).
                    validateMappingFile(file);
                  }
                }}
              />

              {/* Import mode selector */}
              <div className="w-full">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Import Type
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setImportMode("full")}
                    className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      importMode === "full"
                        ? "border-indigo-500 dark:border-violet-500 bg-indigo-50 dark:bg-violet-500/10 text-indigo-700 dark:text-violet-400"
                        : "border-border hover:bg-accent text-muted-foreground"
                    }`}
                  >
                    <span className="block font-semibold">Full</span>
                    <span className="block text-[11px] mt-0.5 font-normal opacity-70">
                      Import entire mapping
                    </span>
                  </button>
                  <button
                    disabled
                    className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-muted-foreground opacity-50 cursor-not-allowed"
                    title="Coming soon"
                  >
                    <span className="block font-semibold">Delta</span>
                    <span className="block text-[11px] mt-0.5 font-normal opacity-70">
                      Coming soon
                    </span>
                  </button>
                </div>
              </div>

              {/* Organization selector */}
              {accessibleOrgs.length > 0 && (
                <div className="w-full">
                  <label className="text-xs font-medium text-muted-foreground block mb-2">
                    Organization
                  </label>
                  <Dropdown
                    value={importOrgId}
                    onChange={setImportOrgId}
                    placeholder="Select Organization"
                    variant="form"
                    options={accessibleOrgs.map((org) => ({
                      value: org.id,
                      label: org.name,
                    }))}
                  />
                </div>
              )}

              {/* Validation findings — resolved catalogs, pickers for the
                  side(s) the file couldn't resolve, and file-level errors. */}
              {mv.hasResult && (
                <div className="w-full space-y-3">
                  {/* Resolved catalogs */}
                  {(mv.resolvedSourceName || mv.resolvedTargetName) && (
                    <div className="flex flex-wrap gap-4">
                      {mv.resolvedSourceName && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                          <CheckCircle className="size-3.5" /> Source:{" "}
                          <span className="font-medium">
                            {mv.resolvedSourceName}
                          </span>
                        </p>
                      )}
                      {mv.resolvedTargetName && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                          <CheckCircle className="size-3.5" /> Target:{" "}
                          <span className="font-medium">
                            {mv.resolvedTargetName}
                          </span>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Catalog list failed to load — offer a retry */}
                  {importCatalogsError &&
                    (mv.missingSource || mv.missingTarget) && (
                      <div className="flex items-center justify-between gap-2 rounded-lg border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10 px-3 py-2">
                        <span className="text-xs text-red-700 dark:text-red-400">
                          Couldn't load catalogs.
                        </span>
                        <button
                          onClick={loadImportCatalogs}
                          className="text-xs font-semibold text-red-700 dark:text-red-400 underline underline-offset-2 hover:opacity-80"
                        >
                          Retry
                        </button>
                      </div>
                    )}

                  {/* Pickers for the side(s) the file couldn't resolve. Also
                      keep a picker visible once a side has been picked, so a
                      wrong pick (unknown-id errors) can be swapped for another. */}
                  {(mv.missingSource ||
                    mv.missingTarget ||
                    importSourceCatalogId ||
                    importTargetCatalogId) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {(mv.missingSource || importSourceCatalogId) && (
                        <div>
                          <label className="text-xs font-medium text-amber-600 dark:text-amber-400 block mb-2">
                            {mv.missingSource
                              ? "Source catalog not resolved — pick one"
                              : "Source catalog — try a different one"}
                          </label>
                          <Dropdown
                            value={importSourceCatalogId}
                            onChange={(v) => {
                              setImportSourceCatalogId(v);
                              // If the target side also still needs a catalog,
                              // wait until it's picked before validating so we
                              // send both ids together.
                              if (mv.missingTarget && !importTargetCatalogId)
                                return;
                              validateMappingFile(importFile ?? undefined, {
                                sourceCatalogId: v,
                              });
                            }}
                            disabled={validating || staging}
                            placeholder="Select source catalog"
                            variant="form"
                            options={importCatalogs
                              // Can't map a catalog to itself — exclude the
                              // catalog resolved (or picked) on the target side.
                              .filter(
                                (c) =>
                                  c.id !== mv.resolvedTargetId &&
                                  c.id !== importTargetCatalogId,
                              )
                              .map((c) => ({
                                value: c.id,
                                label: c.title || c.documentId,
                              }))}
                          />
                        </div>
                      )}
                      {(mv.missingTarget || importTargetCatalogId) && (
                        <div>
                          <label className="text-xs font-medium text-amber-600 dark:text-amber-400 block mb-2">
                            {mv.missingTarget
                              ? "Target catalog not resolved — pick one"
                              : "Target catalog — try a different one"}
                          </label>
                          <Dropdown
                            value={importTargetCatalogId}
                            onChange={(v) => {
                              setImportTargetCatalogId(v);
                              // If the source side also still needs a catalog,
                              // wait until it's picked before validating so we
                              // send both ids together.
                              if (mv.missingSource && !importSourceCatalogId)
                                return;
                              validateMappingFile(importFile ?? undefined, {
                                targetCatalogId: v,
                              });
                            }}
                            disabled={validating || staging}
                            placeholder="Select target catalog"
                            variant="form"
                            options={importCatalogs
                              // Can't map a catalog to itself — exclude the
                              // catalog resolved (or picked) on the source side.
                              .filter(
                                (c) =>
                                  c.id !== mv.resolvedSourceId &&
                                  c.id !== importSourceCatalogId,
                              )
                              .map((c) => ({
                                value: c.id,
                                label: c.title || c.documentId,
                              }))}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* File-level errors (unknown ids / structural) — fix + retry */}
                  {mv.blockingErrors.length > 0 && (
                    <div className="rounded-lg border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10 p-4 space-y-2">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="size-4 mt-0.5 flex-shrink-0 text-red-500" />
                        <p className="text-xs font-semibold text-red-700 dark:text-red-400">
                          {importSourceCatalogId || importTargetCatalogId
                            ? "Validation failed — pick a different catalog above, or fix the file and re-attach it."
                            : "Validation failed — fix the file and re-attach it."}
                        </p>
                      </div>
                      <ul className="space-y-1.5 pl-6">
                        {mv.blockingErrors.map((f, i) => (
                          <li key={f.code ?? i} className="text-[11px]">
                            <span className="font-semibold text-red-700 dark:text-red-400">
                              {f.title || f.code}
                            </span>
                            {f.detail && (
                              <span className="text-red-600 dark:text-red-400/80">
                                {" "}
                                — {f.detail}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <p className="text-sm font-semibold text-foreground">
                Select a source to add from
              </p>
              <div className="flex items-center justify-center gap-8">
                <button
                  onClick={() =>
                    document.getElementById("mapping-file-input")?.click()
                  }
                  className="flex flex-col items-center gap-3 group"
                >
                  <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                    <Upload className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    Upload file
                  </span>
                </button>
                <button
                  onClick={() => setImportStep("url")}
                  className="flex flex-col items-center gap-3 group"
                >
                  <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                    <Link2 className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    Source link
                  </span>
                </button>
              </div>
              {/* Show selected file inline with validation status */}
              {importFile && (
                <div
                  className={`rounded-lg border w-full transition-colors ${
                    validating
                      ? "border-indigo-300 dark:border-violet-500/30 bg-indigo-50 dark:bg-violet-500/10"
                      : mv.clean
                        ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                        : mv.hasErrors || importError
                          ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                          : "border-border bg-muted"
                  }`}
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    {validating ? (
                      <Loader2 className="size-6 flex-shrink-0 text-indigo-500 dark:text-violet-400 animate-spin" />
                    ) : (
                      <FileText
                        className={`size-6 flex-shrink-0 ${
                          mv.clean
                            ? "text-emerald-500"
                            : mv.hasErrors || importError
                              ? "text-red-500"
                              : "text-muted-foreground"
                        }`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {importFile.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(importFile.size / 1024).toFixed(1)} KB
                      </p>
                      {validating && (
                        <p className="text-xs text-indigo-600 dark:text-violet-400 flex items-center gap-1.5 mt-1">
                          <Loader2 className="size-3 animate-spin" />{" "}
                          Validating…
                        </p>
                      )}
                      {!validating && mv.clean && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                          <CheckCircle className="size-3" /> Validated — ready to
                          import
                        </p>
                      )}
                      {!validating &&
                        mv.hasErrors &&
                        (mv.missingSource || mv.missingTarget) &&
                        mv.blockingErrors.length === 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5 mt-1">
                            <AlertTriangle className="size-3" /> Pick the missing
                            catalog above to continue.
                          </p>
                        )}
                      {/* File/network error (bad file, validate request failed) */}
                      {!validating && importError && !mv.hasResult && (
                        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5 mt-1">
                          <AlertTriangle className="size-3" /> {importError}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setImportFile(null);
                        setValidateResult(null);
                        setImportError(null);
                        setValidating(false);
                        setImportSourceCatalogId("");
                        setImportTargetCatalogId("");
                      }}
                      className="p-1 rounded hover:bg-accent flex-shrink-0"
                    >
                      <X className="size-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 1b: URI link */}
          {importStep === "url" && (
            <div className="space-y-4">
              <FormField
                label="Document URL"
                required
                hint="URL must end in .json, .yaml, or .xml"
              >
                <div className="flex gap-2">
                  <div className="flex-1">
                    <FormInput
                      placeholder="https://csrc.nist.gov/projects/oscal/"
                      value={importUrl}
                      onChange={(v) => {
                        setImportUrl(v);
                        setValidateResult(null);
                        setImportError(null);
                      }}
                      disabled={validating}
                    />
                  </div>
                  <button
                    onClick={fetchUrl}
                    disabled={!importUrl.trim() || validating}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      !importUrl.trim() || validating
                        ? "bg-muted text-muted-foreground cursor-not-allowed"
                        : "bg-indigo-600 dark:bg-violet-600 text-white hover:bg-indigo-700 dark:hover:bg-violet-700"
                    }`}
                  >
                    {validating ? (
                      <span className="flex items-center gap-1.5">
                        <Loader2 className="size-3.5 animate-spin" /> Fetching…
                      </span>
                    ) : (
                      "Fetch"
                    )}
                  </button>
                </div>
              </FormField>

              {/* Inline file card */}
              {(validating || validateResult || importError) && (
                <div
                  className={`rounded-lg border w-full ${
                    validateResult
                      ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                      : importError
                        ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                        : "border-border bg-muted"
                  }`}
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    <Link2
                      className={`size-6 flex-shrink-0 ${validateResult ? "text-emerald-500" : importError ? "text-red-500" : "text-muted-foreground"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {importUrl.split("/").pop() || importUrl}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {importUrl}
                      </p>
                      {validating && (
                        <p className="text-xs text-indigo-600 dark:text-violet-400 flex items-center gap-1.5 mt-1">
                          <Loader2 className="size-3 animate-spin" /> Fetching &
                          validating…
                        </p>
                      )}
                      {!validating && validateResult && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                          <CheckCircle className="size-3" /> Validated —{" "}
                          {validateResult.documentType}, OSCAL{" "}
                          {validateResult.oscalVersion}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setValidateResult(null);
                        setImportError(null);
                        setValidating(false);
                      }}
                      className="p-1 rounded hover:bg-accent flex-shrink-0"
                    >
                      <X className="size-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Validating */}
          {importStep === "validating" && (
            <div className="py-12 flex flex-col items-center gap-4">
              <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-violet-500" />
              <p className="text-sm font-medium">Validating document…</p>
              <p className="text-xs text-muted-foreground text-center">
                Checking OSCAL schema and document structure.
              </p>
            </div>
          )}

          {/* Validated successfully */}
          {importStep === "validated" && validateResult && (
            <div className="py-8 flex flex-col items-center gap-4 text-center">
              <div className="size-12 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="size-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="text-sm font-semibold">Validation Passed</p>
              <div className="px-4 py-3 rounded-lg bg-muted border border-border w-full text-left space-y-1.5">
                <p className="text-xs">
                  <span className="text-muted-foreground">Document Type:</span>{" "}
                  <span className="font-medium">
                    {validateResult.documentType}
                  </span>
                </p>
                <p className="text-xs">
                  <span className="text-muted-foreground">OSCAL Version:</span>{" "}
                  <span className="font-medium">
                    {validateResult.oscalVersion}
                  </span>
                </p>
                {importFile && (
                  <p className="text-xs">
                    <span className="text-muted-foreground">File:</span>{" "}
                    <span className="font-medium">{importFile.name}</span>
                  </p>
                )}
                {!importFile && importUrl && (
                  <p className="text-xs">
                    <span className="text-muted-foreground">Source:</span>{" "}
                    <span className="font-medium truncate">{importUrl}</span>
                  </p>
                )}
                <p className="text-xs">
                  <span className="text-muted-foreground">Import Mode:</span>{" "}
                  <span className="font-medium capitalize">{importMode}</span>
                </p>
                {validateResult.findings.length > 0 && (
                  <div className="pt-1.5 border-t border-border mt-1.5">
                    <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 mb-1">
                      Findings ({validateResult.findings.length})
                    </p>
                    {validateResult.findings.map((f, i) => (
                      <p key={i} className="text-xs text-muted-foreground">
                        {f.severity}: {f.message}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Importing */}
          {importStep === "importing" && (
            <div className="py-12 flex flex-col items-center gap-4">
              <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-violet-500" />
              <p className="text-sm font-medium">Importing document…</p>
              <p className="text-xs text-muted-foreground text-center">
                Uploading and processing your OSCAL mapping. This may take a
                moment for large files.
              </p>
            </div>
          )}
        </Modal>

        {/* ── Import Session Modal ── */}
        <Modal
          open={showSession}
          onClose={() => {
            setShowSession(false);
            setSessionData(null);
          }}
          title={
            sessionData
              ? sessionData.jobStatus.toLowerCase() === "committed"
                ? "Import Complete"
                : sessionData.jobStatus.toLowerCase() === "validated"
                  ? "Commit Mapping"
                  : sessionData.jobStatus.toLowerCase() === "failed" ||
                      sessionData.jobStatus.toLowerCase() === "aborted"
                    ? "Import Failed"
                    : "Importing Mapping"
              : "Import Mapping"
          }
          size="md"
          closable={false}
          footer={
            sessionData ? (
              <div className="flex justify-end w-full gap-2">
                {sessionData.jobStatus.toLowerCase() === "committed" ? (
                  <button
                    onClick={() => {
                      setShowSession(false);
                      setSessionData(null);
                      resetImport();
                    }}
                    className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-violet-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-violet-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-violet-500/20"
                  >
                    Done
                  </button>
                ) : sessionData.jobStatus.toLowerCase() === "failed" ||
                  sessionData.jobStatus.toLowerCase() === "aborted" ? (
                  <button
                    onClick={() => {
                      setShowSession(false);
                      setSessionData(null);
                      resetImport();
                    }}
                    className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                  >
                    Close
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleAbortSession}
                      disabled={aborting || committing}
                      className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40"
                    >
                      {aborting ? "Cancelling…" : "Cancel"}
                    </button>
                    {(() => {
                      // Block commit on any error finding (e.g. duplicate title),
                      // even when the document validated.
                      const hasErrorFinding = (sessionData.findings ?? []).some(
                        (f) => f.severity?.toLowerCase() === "error",
                      );
                      const commitDisabled =
                        committing ||
                        aborting ||
                        hasErrorFinding ||
                        sessionData.jobStatus.toLowerCase() !== "validated";
                      return (
                        <Tip
                          label={
                            hasErrorFinding
                              ? "Resolve the errors above before committing"
                              : ""
                          }
                          side="top"
                        >
                          <button
                            onClick={handleCommitSession}
                            disabled={commitDisabled}
                            className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${
                              commitDisabled
                                ? "bg-indigo-400 dark:bg-violet-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-violet-500/10"
                                : "bg-indigo-600 dark:bg-violet-600 hover:bg-indigo-700 dark:hover:bg-violet-700 shadow-indigo-500/20 dark:shadow-violet-500/20"
                            }`}
                          >
                            {committing ? "Committing…" : "Commit"}
                          </button>
                        </Tip>
                      );
                    })()}
                  </>
                )}
              </div>
            ) : null
          }
        >
          {sessionLoading && (
            <div className="py-12 flex flex-col items-center gap-4">
              <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-violet-500" />
              <p className="text-sm font-medium">Loading session…</p>
            </div>
          )}

          {sessionError && (
            <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400 mb-4">
              <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
              <p className="text-xs">{sessionError}</p>
            </div>
          )}

          {sessionData && (
            <div className="space-y-5">
              {committing && (
                <div className="py-6 flex flex-col items-center gap-3">
                  <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-violet-500" />
                  <p className="text-sm font-medium">Committing import…</p>
                  <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-indigo-500 dark:bg-violet-500 animate-pulse"
                      style={{ width: "70%" }}
                    />
                  </div>
                </div>
              )}

              {!committing && (
                <>
                  <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                    <div className="flex items-center justify-between py-1.5 border-b border-border">
                      <span className="text-xs text-muted-foreground">
                        Status
                      </span>
                      <span
                        className={`text-sm font-semibold capitalize ${
                          sessionData.jobStatus.toLowerCase() === "committed"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : sessionData.jobStatus.toLowerCase() ===
                                  "failed" ||
                                sessionData.jobStatus.toLowerCase() ===
                                  "aborted"
                              ? "text-red-600 dark:text-red-400"
                              : sessionData.jobStatus.toLowerCase() ===
                                  "validated"
                                ? "text-indigo-600 dark:text-violet-400"
                                : "text-amber-600 dark:text-amber-400"
                        }`}
                      >
                        {sessionData.jobStatus}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-border">
                      <span className="text-xs text-muted-foreground">
                        Doc Type
                      </span>
                      <span className="text-sm font-medium capitalize">
                        {sessionData.documentType}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1.5">
                      <span className="text-xs text-muted-foreground">
                        Name
                      </span>
                      <span className="text-sm font-medium">
                        {sessionData.sourceFilename}
                      </span>
                    </div>
                  </div>

                  {sessionData.findings.length > 0 && (
                    <div className="rounded-lg border border-border p-4">
                      <h4 className="text-xs font-bold text-muted-foreground mb-2">
                        Findings ({sessionData.findings.length})
                      </h4>
                      <div className="space-y-2">
                        {sessionData.findings.map((f) => (
                          <div
                            key={f.id}
                            className="flex items-start gap-2 text-xs"
                          >
                            <span
                              className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${
                                f.severity === "info"
                                  ? "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400"
                                  : f.severity === "warning"
                                    ? "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                                    : "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
                              }`}
                            >
                              {f.severity}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium">{f.title}</p>
                              <p className="text-muted-foreground">
                                {f.detail}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Modal>

        {/* ── Export Pulse Overlay ── */}
        {exportingId && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 bg-white dark:bg-zinc-900 rounded-2xl px-10 py-8 shadow-2xl">
              <div className="relative flex items-center justify-center">
                <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-violet-500/20 animate-ping" />
                <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-violet-500/30 animate-pulse" />
                <Upload className="relative size-6 text-indigo-600 dark:text-violet-400" />
              </div>
              <p className="text-sm font-semibold text-foreground mt-2">
                Exporting as {exportFormat.toUpperCase()}
              </p>
              <p className="text-xs text-muted-foreground">
                Please wait while we prepare your file
              </p>
            </div>
          </div>
        )}

        {/* ── Export Format Modal ── */}
        <Modal
          open={!!exportModalDocId}
          onClose={() => setExportModalDocId(null)}
          title="Export Mapping"
          description={`Choose a file format to export "${exportModalName}".`}
          size="sm"
        >
          <div className="grid grid-cols-3 gap-3">
            {(["json", "xml", "yaml"] as const).map((fmt) => (
              <button
                key={fmt}
                onClick={() => {
                  if (exportModalDocId)
                    handleExportMapping(exportModalDocId, fmt);
                }}
                disabled={exportingId === exportModalDocId}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-indigo-400 dark:hover:border-violet-400 hover:bg-indigo-50 dark:hover:bg-violet-500/10 transition-colors group disabled:opacity-40"
              >
                {exportingId === exportModalDocId ? (
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                ) : (
                  <Upload className="size-6 text-muted-foreground group-hover:text-indigo-600 dark:group-hover:text-violet-400 transition-colors" />
                )}
                <span className="text-sm font-semibold group-hover:text-indigo-600 dark:group-hover:text-violet-400 transition-colors">
                  {fmt.toUpperCase()}
                </span>
              </button>
            ))}
          </div>
        </Modal>
      </div>
    );
  }

  return null;
}
